import { type PayloadAction, createAsyncThunk, createSlice, nanoid } from '@reduxjs/toolkit'
import axios from 'axios'

import { apiZodios } from '~api/apiZodios'
import { type FlowIssue } from '~pages/ProcessesScreen/components/ProcessFlowEditor/IssuesFlyoutMenu'
import { type BlockConfigurations } from '~pages/ProcessesScreen/hooks/useProcessFlowData'
import { type IntegrationBlockMetadata } from '~pages/ProcessesScreen/utils/integrationBlockUtils'
import {
  type BillStep1,
  type BillStepId,
  type Block,
  type BlockId,
  type CalculatedTag,
  type CaseRoleAutomation,
  type CaseStatusAutomation,
  type CaseStatusEnum,
  type ChargeSubStepCreate,
  type ChargeSubstep,
  type CommunicationAutomation,
  type CommunicationSourceEmails,
  type Connector,
  type ConnectorId,
  type CopySectionSubStepViewRequestData,
  type CreateCommunicationBlockViewRequestData,
  type CreateDocumentBlockViewRequestData,
  type CreateFormStyleElementViewRequestData,
  type CreateInputStepViewRequestData,
  type CreateRoleBlockViewRequestData,
  type CreateSectionStepViewRequestData,
  type CreateTagBlockViewRequestData,
  type DatetimeStep,
  type DeleteFormStyleElementViewRequestData,
  type DeleteInputStepViewRequestData,
  type DeleteSectionSubstepViewRequestData,
  type DeleteStepAssignmentViewRequestData,
  type DocumentStep,
  type EditAPIAutomationBlockViewRequestData,
  type EditBillStepViewRequestData,
  type EditCommunicationBlockViewRequestData,
  type EditDateTimeStepViewRequestData,
  type EditDemoBlockViewRequestData,
  type EditFileStepViewRequestData,
  type EditFormStepViewRequestData,
  type EditFormStyleElementRequestData,
  type EditIdentityStepViewRequestData,
  type EditInputStepViewRequestData,
  type EditLicenseBlockViewRequestData,
  type EditLocationStepViewRequestData,
  type EditNumberStepViewRequestData,
  type EditProcessStageViewRequestData,
  type EditRedactionBlockViewRequestData,
  type EditReviewBlockViewRequestData,
  type EditRoleBlockViewRequestData,
  type EditSectionSubStepViewRequestData,
  type EditSignatureStepViewRequestData,
  type EditStepViewRequestData,
  type EditTagBlockViewRequestData,
  type Expression,
  type ExpressionId,
  type FileStep,
  type FormNodeTypeEnum,
  type FormStep0,
  type FormStepId,
  type FormStyleElement,
  type FormStyleElementId,
  type FormStyleElementType,
  type IdentityId,
  type IdentityStep,
  type InputStepChoice1,
  type InputStepId,
  type InputStepSub0,
  type InputStyleEnum,
  type InputSubstep2,
  type LicenseApplicationType,
  type LicenseTemplateId,
  type LocationStep,
  type NumberStep,
  OperatorType,
  type Process3,
  type ProcessId,
  type ProcessStageId,
  type ReviewOutcome,
  type SectionStepId,
  type SectionSubstep0,
  type SignatureStep,
  type StepId,
  type StepNotification,
  StepType,
  type TagAutomationAction,
  type TagAutomationTarget,
  type TagId,
  type Value0,
  type ValueId,
  type ValueType,
  ValueTypeEnum,
} from '~types'

export type SelectedStep = {
  stepId: FormStepId | BillStepId
  openEditor: boolean
  type: StepType
}

export type SelectedSubstep = {
  substepId: InputStepId | SectionStepId | FormStyleElementId
  type: FormNodeTypeEnum | 'page_break'
}

export enum ModalType {
  publish = 'publish',
  preview = 'preview',
  share = 'share',
  expression = 'expression',
  assignees = 'assignees',
  billStep = 'billStep',
  tags = 'tags',
  documentBlock = 'documentBlock',
}

export enum DatabaseSaveStatus {
  idle = 'idle',
  loading = 'loading',
  succeeded = 'succeeded',
  failed = 'failed',
}

// 'custom' builds a boolean expression that is persisted as the block's
// entry_criteria (gate_type falls back to any_inbound for that block).
export type BlockJoinType = 'all' | 'any' | 'custom'

export interface SnackbarConfiguration {
  show: boolean
  message: string
  severity: 'success' | 'error' | 'info' | 'warning'
}

interface ProcessEditState {
  currentProcess: Process3 | null
  selectedStage: boolean
  selectedStep: SelectedStep | null
  selectedStepCheckboxes: StepId[]
  selectedBlockCheckboxes: BlockId[]
  selectedFormSubstep: SelectedSubstep | null
  hasTrackerStageError: boolean
  trackerStageErrorMessage: string
  status: DatabaseSaveStatus
  error: string | null
  // Message from the backend that is safe to show to the user verbatim
  errorDetail: string | null
  userId: string | null // UserId
  expressions: Record<ExpressionId, Expression>
  modal: ModalType | null
  stepNotifications: Record<BlockId, StepNotification[]>
  snackbarConfiguration: SnackbarConfiguration
  flowBlocks: Block[]
  flowConnectors: Connector[]
  flowBlockConfigurations: BlockConfigurations | null
  communicationAutomations: Record<string, CommunicationAutomation>
  selectedCommunicationBlock: BlockId | null
  selectedRedactionBlock: BlockId | null
  emailCapability: CommunicationSourceEmails | null
  // Local-only join semantics ('All of' / 'Any of') for blocks with multiple
  // incoming connectors. Persistence is wired up once the backend supports it.
  blockJoinTypes: Record<string, BlockJoinType>
  // Root expression id per block for the 'custom' join option, mirrored locally
  // for instant feedback. The expression is persisted as block.entry_criteria.
  blockJoinExpressionIds: Record<string, ExpressionId>
  flowIssues: FlowIssue[]
}

const initialState: ProcessEditState = {
  currentProcess: null,
  selectedStage: false,
  selectedStep: null,
  selectedStepCheckboxes: [],
  selectedBlockCheckboxes: [],
  selectedFormSubstep: null,
  hasTrackerStageError: false,
  trackerStageErrorMessage: '',
  status: DatabaseSaveStatus.idle,
  error: null,
  errorDetail: null,
  userId: null,
  expressions: {},
  modal: null,
  stepNotifications: {},
  snackbarConfiguration: {
    show: false,
    message: '',
    severity: 'success',
  },
  flowBlocks: [],
  flowConnectors: [],
  flowBlockConfigurations: null,
  communicationAutomations: {},
  selectedCommunicationBlock: null,
  selectedRedactionBlock: null,
  emailCapability: null,
  blockJoinTypes: {},
  blockJoinExpressionIds: {},
  flowIssues: [],
}

export const createInputStep = createAsyncThunk(
  'processEdit/createInputStep',
  async (
    params: Omit<CreateInputStepViewRequestData, 'user_id' | 'organization_identity_id'> & {
      temporaryId: string
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID & Organization ID are required but not found in state')
      }

      const { input_step: inputStep } = await apiZodios.cases_createInputStep({
        user_id: userId,
        organization_identity_id: processOwnerId,
        value_type: params.value_type as ValueType,
        name: params.name as string,
        description: params.description as string,
        template: params.template as ValueId | null,
        style: params.style as InputStyleEnum,
        required: params.required as boolean,
        section: (params.section ?? null) as SectionStepId | null,
        form: params.form as FormStepId,
        pinned: params.pinned as boolean,
        order: params.order as number,
        process_id: params.process_id as ProcessId,
      })

      return {
        inputStepId: inputStep.id,
        datetimeStepSub: inputStep?.sub?.datetime_step_sub,
        identityStepSub: inputStep?.sub?.identity_step_sub,
        numberStepSub: inputStep?.sub?.number_step_sub,
        fileStepSub: inputStep?.sub?.file_step_sub,
        locationStepSub: inputStep?.sub?.location_step_sub,
        order: params.order,
        temporaryId: params.temporaryId,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createProcessStage = createAsyncThunk(
  'processEdit/createProcessStage',
  async (
    params: { process_id: string; order: number } & {
      temporary_id: string
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID and Organization ID are required but not found in state')
      }

      const response = await apiZodios.cases_createProcessStage({
        user_id: userId,
        organization_identity_id: processOwnerId,
        name: '',
        description: '',
        order: params.order as number,
        process_id: params.process_id as ProcessId,
      })

      return {
        processStageId: response.process_stage_id,
        order: params.order,
        temporaryId: params.temporary_id,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createFormStyleElement = createAsyncThunk(
  'processEdit/createFormStyleElement',
  async (
    params: Omit<CreateFormStyleElementViewRequestData, 'user_id' | 'organization_identity_id'> & {
      temporary_id: string
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID & Organization ID are required but not found in state')
      }

      const { form_style_element: formStyleElement } = await apiZodios.cases_createFormStyleElement(
        {
          user_id: userId,
          organization_identity_id: processOwnerId,
          form_step_id: params.form_step_id as string,
          order: params.order as number,
          type: params.type as FormStyleElementType,
        },
      )
      return {
        temporaryId: params.temporary_id,
        formStyleElementId: formStyleElement.id,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createSectionStep = createAsyncThunk(
  'processEdit/createSectionStep',
  async (
    params: Omit<CreateSectionStepViewRequestData, 'user_id' | 'organization_identity_id'> & {
      temporaryId: string
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID & Organization ID are required but not found in state')
      }

      const response = await apiZodios.cases_createSectionStep({
        user_id: userId,
        organization_identity_id: processOwnerId,
        name: params.name as string,
        description: params.description as string,
        section: params.section as SectionStepId | null,
        form: params.form as FormStepId,
        order: params.order as number,
        button_name: params.button_name as string,
        min: params.min as number,
        max: params.max as number,
        render: params.render as ExpressionId | null,
      })

      return {
        sectionStepId: response.section_step_id,
        order: params.order,
        temporaryId: params.temporaryId,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createTagBlock = createAsyncThunk(
  'processEdit/createTagBlock',
  async (
    params: Omit<
      CreateTagBlockViewRequestData,
      'user_id' | 'organization_identity_id' | 'workflow_version_id'
    > & { temporaryId?: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const process = state.processEdit.currentProcess

      if (!userId || !process) {
        return rejectWithValue('User ID and Process are required but not found in state')
      }

      const { block: block, tag_automation: tagAutomation } = await apiZodios.cases_createTagBlock({
        user_id: userId,
        organization_identity_id: process.owner.id,
        workflow_version_id: process.id,
        ...(params.step_id ? { step_id: params.step_id as StepId } : {}),
        ...(params.tag_id ? { tag_id: params.tag_id as TagId } : {}),
        ...(params.apply_id ? { apply_id: params.apply_id as ExpressionId } : {}),
      })

      return {
        block,
        blockId: block.id,
        tagAutomation: tagAutomation,
        stepId: params.step_id,
        temporaryId: params.temporaryId,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createRoleBlock = createAsyncThunk(
  'processEdit/createRoleBlock',
  async (
    params: Omit<
      CreateRoleBlockViewRequestData,
      'user_id' | 'organization_identity_id' | 'workflow_version_id'
    > & { temporaryId?: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const process = state.processEdit.currentProcess

      if (!userId || !process) {
        return rejectWithValue('User ID and Process are required but not found in state')
      }

      const { block, role_automation: roleAutomation } = await apiZodios.cases_createRoleBlock({
        user_id: userId,
        organization_identity_id: process.owner.id,
        workflow_version_id: process.id,
      })

      return {
        block,
        blockId: block.id,
        roleAutomation,
        temporaryId: params.temporaryId,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createCommunicationBlock = createAsyncThunk(
  'processEdit/createCommunicationBlock',
  async (
    params: Omit<
      CreateCommunicationBlockViewRequestData,
      'user_id' | 'organization_identity_id' | 'workflow_version_id'
    > & { temporaryId?: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const process = state.processEdit.currentProcess

      if (!userId || !process) {
        return rejectWithValue('User ID and Process are required but not found in state')
      }

      const { block, communication_automation: communicationAutomation } =
        await apiZodios.cases_createCommunicationBlock({
          user_id: userId,
          organization_identity_id: process.owner.id,
          workflow_version_id: process.id,
        })

      return {
        block,
        blockId: block.id as BlockId,
        communicationAutomation,
        temporaryId: params.temporaryId,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editCommunicationBlock = createAsyncThunk(
  'processEdit/editCommunicationBlock',
  async (
    params: Omit<EditCommunicationBlockViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      if (!currentProcess?.owner?.id) {
        return rejectWithValue('Process owner ID is required but not found in state')
      }

      const response = await apiZodios.cases_editCommunicationBlock({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        block_id: params.block_id,
        ...(params.name !== undefined && { name: params.name }),
        ...(params.subject !== undefined && { subject: params.subject }),
        ...(params.staff_review_required !== undefined && {
          staff_review_required: params.staff_review_required,
        }),
        ...('from_email' in params && { from_email: params.from_email }),
        ...('form_letter_template' in params && {
          form_letter_template: params.form_letter_template,
        }),
        ...(params.ai_prompt !== undefined && { ai_prompt: params.ai_prompt }),
        ...(params.body !== undefined && { body: params.body }),
        ...(params.input_step_ids !== undefined && { input_step_ids: params.input_step_ids }),
        ...(params.attachments !== undefined && { attachments: params.attachments }),
        ...(params.case_document_step_ids !== undefined && {
          case_document_step_ids: params.case_document_step_ids,
        }),
        ...(params.case_file_input_step_ids !== undefined && {
          case_file_input_step_ids: params.case_file_input_step_ids,
        }),
        ...(params.case_redaction_block_ids !== undefined && {
          case_redaction_block_ids: params.case_redaction_block_ids,
        }),
        ...(params.case_document_ai_enabled !== undefined && {
          case_document_ai_enabled: params.case_document_ai_enabled,
        }),
        ...(params.recipients !== undefined && { recipients: params.recipients }),
        ...('due' in params && { due: params.due }),
      })

      return {
        blockId: params.block_id,
        communicationAutomation: response.communication_automation,
      }
    } catch (error) {
      if (axios.isAxiosError(error) && typeof error.response?.data?.detail === 'string') {
        return rejectWithValue(error.response.data.detail)
      }
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createDocumentBlock = createAsyncThunk(
  'processEdit/createDocumentBlock',
  async (
    params: Omit<
      CreateDocumentBlockViewRequestData,
      'user_id' | 'organization_identity_id' | 'workflow_version_id'
    > & { temporaryId?: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const process = state.processEdit.currentProcess

      if (!userId || !process) {
        return rejectWithValue('User ID and Process are required but not found in state')
      }

      const { block } = await apiZodios.cases_createDocumentGenerationBlock({
        user_id: userId,
        organization_identity_id: process.owner.id,
        workflow_version_id: process.id,
      })

      return {
        block,
        blockId: block.id,
        temporaryId: params.temporaryId,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createDemoBlock = createAsyncThunk(
  'processEdit/createDemoBlock',
  async (params: { temporaryId?: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const process = state.processEdit.currentProcess

      if (!userId || !process) {
        return rejectWithValue('User ID and Process are required but not found in state')
      }

      const { block, demo_block: demoBlock } = await apiZodios.cases_createDemoBlock({
        user_id: userId,
        organization_identity_id: process.owner.id,
        workflow_version_id: process.id,
      })

      return {
        block,
        demoBlock,
        blockId: block.id,
        temporaryId: params.temporaryId,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createAPIAutomationBlock = createAsyncThunk(
  'processEdit/createAPIAutomationBlock',
  async (
    params: {
      temporaryId?: string
      metadata: IntegrationBlockMetadata
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const process = state.processEdit.currentProcess

      if (!userId || !process) {
        return rejectWithValue('User ID and Process are required but not found in state')
      }

      const { block, api_automation: apiAutomation } =
        await apiZodios.cases_createAPIAutomationBlock({
          user_id: userId,
          organization_identity_id: process.owner.id,
          process_version: process.id,
          connection_type: params.metadata.connection_type,
          endpoint_tool_name: params.metadata.endpoint_tool_name,
        })

      return {
        block,
        apiAutomation,
        blockId: block.id,
        temporaryId: params.temporaryId,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editAPIAutomationBlock = createAsyncThunk(
  'processEdit/editAPIAutomationBlock',
  async (
    params: Omit<EditAPIAutomationBlockViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId || !currentProcess?.owner?.id) {
        return rejectWithValue('User ID and Process are required but not found in state')
      }

      const response = await apiZodios.cases_editAPIAutomationBlock({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        ...params,
      })

      return {
        blockId: params.block_id,
        apiAutomation: response.api_automation,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editDemoBlock = createAsyncThunk(
  'processEdit/editDemoBlock',
  async (
    params: Omit<EditDemoBlockViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId || !currentProcess?.owner?.id) {
        return rejectWithValue('User ID and Process are required but not found in state')
      }

      const response = await apiZodios.cases_editDemoBlock({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        block_id: params.block_id,
        ...(params.name !== undefined ? { name: params.name } : {}),
        ...(params.description !== undefined ? { description: params.description } : {}),
        ...(params.icon !== undefined ? { icon: params.icon } : {}),
        ...(params.color !== undefined ? { color: params.color } : {}),
      })

      return {
        blockId: params.block_id,
        demoBlock: response.demo_block,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createRedactionBlock = createAsyncThunk(
  'processEdit/createRedactionBlock',
  async (params: { temporaryId?: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const process = state.processEdit.currentProcess

      if (!userId || !process) {
        return rejectWithValue('User ID and Process are required but not found in state')
      }

      const { block, redaction_block: redactionBlock } = await apiZodios.cases_createRedactionBlock(
        {
          user_id: userId,
          organization_identity_id: process.owner.id,
          workflow_version_id: process.id,
        },
      )

      return {
        block,
        redactionBlock,
        blockId: block.id,
        temporaryId: params.temporaryId,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editRedactionBlock = createAsyncThunk(
  'processEdit/editRedactionBlock',
  async (
    params: Omit<EditRedactionBlockViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId || !currentProcess?.owner?.id) {
        return rejectWithValue('User ID and Process are required but not found in state')
      }

      const response = await apiZodios.cases_editRedactionBlock({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        block_id: params.block_id,
        name: params.name,
      })

      return {
        blockId: params.block_id,
        redactionBlock: response.redaction_block,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createReviewBlock = createAsyncThunk(
  'processEdit/createReviewBlock',
  async (params: { temporaryId?: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const process = state.processEdit.currentProcess

      if (!userId || !process) {
        return rejectWithValue('User ID and Process are required but not found in state')
      }

      const {
        block,
        review_block: reviewBlock,
        connectors,
        gate_expressions: gateExpressions,
      } = await apiZodios.cases_createReviewBlock({
        user_id: userId,
        organization_identity_id: process.owner.id,
        workflow_version_id: process.id,
      })

      return {
        block,
        reviewBlock,
        connectors,
        gateExpressions,
        blockId: block.id as BlockId,
        temporaryId: params.temporaryId,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editReviewBlock = createAsyncThunk(
  'processEdit/editReviewBlock',
  async (
    params: Omit<EditReviewBlockViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId || !currentProcess?.owner?.id) {
        return rejectWithValue('User ID and Process are required but not found in state')
      }

      const {
        review_block: reviewBlock,
        connectors,
        gate_expressions: gateExpressions,
        deleted_connector_ids: deletedConnectorIds,
      } = await apiZodios.cases_editReviewBlock({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        ...params,
      })

      return {
        blockId: params.block_id,
        reviewBlock,
        connectors,
        gateExpressions,
        deletedConnectorIds,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createLicenseBlock = createAsyncThunk(
  'processEdit/createLicenseBlock',
  async (params: { temporaryId?: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const process = state.processEdit.currentProcess

      if (!userId || !process) {
        return rejectWithValue('User ID and Process are required but not found in state')
      }

      const { block, license_block: licenseBlock } = await apiZodios.cases_createLicenseBlock({
        user_id: userId,
        organization_identity_id: process.owner.id,
        workflow_version_id: process.id,
      })

      return {
        block,
        licenseBlock,
        blockId: block.id,
        temporaryId: params.temporaryId,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editLicenseBlock = createAsyncThunk(
  'processEdit/editLicenseBlock',
  async (
    params: Omit<EditLicenseBlockViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      if (!currentProcess?.owner?.id) {
        return rejectWithValue('Process owner ID is required but not found in state')
      }

      const response = await apiZodios.cases_editLicenseBlock({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        block_id: params.block_id,
        ...('license_template_id' in params
          ? { license_template_id: params.license_template_id }
          : {}),
        ...(params.application_type ? { application_type: params.application_type } : {}),
      })

      return {
        blockId: params.block_id,
        licenseBlock: response.license_block,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const copySectionStep = createAsyncThunk(
  'processEdit/copySectionStep',
  async (
    params: Omit<CopySectionSubStepViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID & Organization ID are required but not found in state')
      }

      const {
        section_step_id: sectionStepId,
        copied_sections: copiedSections,
        copied_inputs: copiedInputs,
      } = await apiZodios.cases_copySectionSubStep({
        user_id: userId,
        organization_identity_id: processOwnerId,
        section_step_id: params.section_step_id as SectionStepId,
        section_id: params.section_id as SectionStepId | null,
        form_step_id: params.form_step_id as FormStepId,
        order: params.order as number,
      })
      return {
        sectionStepId: sectionStepId,
        copiedSections: copiedSections,
        copiedInputs: copiedInputs,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const deleteBillStep = createAsyncThunk(
  'processEdit/deleteBillStep',
  async (params: { bill_step_id: BillStepId }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID and Organization ID are required but not found in state')
      }

      await apiZodios.cases_deleteBillStep({
        user_id: userId,
        organization_identity_id: processOwnerId as IdentityId,
        bill_step_id: params.bill_step_id,
      })

      return {}
    } catch (error) {
      if (axios.isAxiosError(error) && typeof error.response?.data?.detail === 'string') {
        return rejectWithValue({ detail: error.response.data.detail })
      }
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const deleteFormStep = createAsyncThunk(
  'processEdit/deleteFormStep',
  async (params: { form_step_id: FormStepId }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID and Organization ID are required but not found in state')
      }

      await apiZodios.cases_deleteFormStep({
        user_id: userId,
        organization_identity_id: processOwnerId as IdentityId,
        form_step_id: params.form_step_id,
      })

      return {}
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const deleteProcessStage = createAsyncThunk(
  'processEdit/deleteProcessStage',
  async (params: { process_stage_id: ProcessStageId }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID and Organization ID are required but not found in state')
      }

      await apiZodios.cases_deleteProcessStage({
        user_id: userId,
        organization_identity_id: processOwnerId as IdentityId,
        process_stage_id: params.process_stage_id,
      })

      return {}
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const deleteFormStyleElement = createAsyncThunk(
  'processEdit/deleteFormStyleElement',
  async (
    params: Omit<DeleteFormStyleElementViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID and Organization ID are required but not found in state')
      }

      await apiZodios.cases_deleteFormStyleElement({
        user_id: userId,
        organization_identity_id: processOwnerId,
        form_style_element_id: params.form_style_element_id as FormStyleElementId,
      })

      return {
        formStyleElementId: params.form_style_element_id,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const deleteInputStep = createAsyncThunk(
  'processEdit/deleteInputStep',
  async (
    params: Omit<DeleteInputStepViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID and Organization ID are required but not found in state')
      }

      const response = await apiZodios.cases_deleteInputSubstep({
        user_id: userId,
        organization_identity_id: processOwnerId,
        input_step_id: params.input_step_id as string,
      })

      return {
        stepId: params.input_step_id,
        response,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const deleteSectionStep = createAsyncThunk(
  'processEdit/deleteSectionStep',
  async (
    params: Omit<DeleteSectionSubstepViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID and Organization ID are required but not found in state')
      }

      const response = await apiZodios.cases_deleteSectionSubstep({
        user_id: userId,
        organization_identity_id: processOwnerId,
        section_substep_id: params.section_substep_id as string,
      })

      return {
        stepId: params.section_substep_id,
        response,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editDatetimeStep = createAsyncThunk(
  'processEdit/editDatetimeStep',
  async (
    params: Omit<EditDateTimeStepViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }
      if (!currentProcess) {
        return rejectWithValue('Current process is required but not found in state')
      }

      const response = await apiZodios.cases_editDateTimeStep({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        datetime_step_id: params.datetime_step_id as string,
        request_time: params.request_time as boolean,
      })

      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editFileStep = createAsyncThunk(
  'processEdit/editFileStep',
  async (
    params: Omit<EditFileStepViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }
      if (!currentProcess) {
        return rejectWithValue('Current process is required but not found in state')
      }

      const response = await apiZodios.cases_editFileStep({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        ...params,
      })
      return { response }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editLocationStep = createAsyncThunk(
  'processEdit/editLocationStep',
  async (
    params: Omit<EditLocationStepViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID and Organization ID are required but not found in state')
      }

      const response = await apiZodios.cases_editLocationStep({
        user_id: userId,
        organization_identity_id: processOwnerId,
        ...params,
      })
      return { response }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editIdentityStep = createAsyncThunk(
  'processEdit/editIdentityStep',
  async (
    params: Omit<EditIdentityStepViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }
      if (!currentProcess) {
        return rejectWithValue('Current process is required but not found in state')
      }

      const response = await apiZodios.cases_editIdentityStep({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        ...params,
      })
      return {
        response,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editInputStep = createAsyncThunk(
  'processEdit/editInputStep',
  async (
    params: Omit<EditInputStepViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      if (!currentProcess?.owner?.id) {
        return rejectWithValue('Process owner ID is required but not found in state')
      }

      const response = await apiZodios.cases_editInputStep({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        ...params,
      })

      return {
        stepId: params.input_step_id,
        updates: params,
        response,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editNumberStep = createAsyncThunk(
  'processEdit/editNumberStep',
  async (
    params: Omit<EditNumberStepViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }
      if (!currentProcess) {
        return rejectWithValue('Current process is required but not found in state')
      }

      const response = await apiZodios.cases_editNumberStep({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        ...params,
      })
      return { response }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editSignatureStep = createAsyncThunk(
  'processEdit/editSignatureStep',
  async (
    params: Omit<EditSignatureStepViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }
      if (!currentProcess) {
        return rejectWithValue('Current process is required but not found in state')
      }

      const response = await apiZodios.cases_editSignatureStep({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        ...params,
      })
      return { response }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editSectionStep = createAsyncThunk(
  'processEdit/editSectionStep',
  async (
    params: Omit<EditSectionSubStepViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      if (!currentProcess?.owner?.id) {
        return rejectWithValue('Process owner ID is required but not found in state')
      }

      const response = await apiZodios.cases_editSectionSubStep({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        ...params,
      })

      return {
        stepId: params.section_step_id,
        updates: params,
        response,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editBillStep = createAsyncThunk(
  'processEdit/editBillStep',
  async (
    params: Omit<EditBillStepViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }
      if (!currentProcess) {
        return rejectWithValue('Current process is required but not found in state')
      }
      const { bill_step: updatedBillStep } = await apiZodios.cases_editBillStep({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        name: params.name as string,
        description: params.description as string,
        order: params.order as number,
        charges: params.charges as ChargeSubStepCreate[],
        ...params,
      })

      return {
        stepId: params.bill_step_id,
        updates: params,
        charges: updatedBillStep.charges,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editFormStep = createAsyncThunk(
  'processEdit/editFormStep',
  async (
    params: Omit<EditFormStepViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }
      if (!currentProcess) {
        return rejectWithValue('Current process is required but not found in state')
      }

      const response = await apiZodios.cases_editFormStep({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        ...params,
      })

      return {
        stepId: params.form_step_id,
        updates: params,
        response,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editProcessStage = createAsyncThunk(
  'processEdit/editProcessStage',
  async (
    params: Omit<EditProcessStageViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID and Organization ID are required but not found in state')
      }

      await apiZodios.cases_editProcessStage({
        user_id: userId,
        organization_identity_id: processOwnerId as IdentityId,
        ...params,
      })
      return { processStageId: params.process_stage_id, updates: params }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editFormStyleElement = createAsyncThunk(
  'processEdit/editFormStyleElement',
  async (
    params: Omit<EditFormStyleElementRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }
      if (!currentProcess) {
        return rejectWithValue('Current process is required but not found in state')
      }

      await apiZodios.cases_editFormStyleElement({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        ...params,
      })

      return {
        formStyleElementId: params.form_style_element_id,
        updates: params,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editStep = createAsyncThunk(
  'processEdit/editStep',
  async (
    params: Omit<EditStepViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      if (!currentProcess?.owner?.id) {
        return rejectWithValue('Process owner ID is required but not found in state')
      }

      const response = await apiZodios.cases_editStep({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        step_id: params.step_id as string,
        set_status: params.set_status as CaseStatusEnum,
        calculated_tags: params.calculated_tags as CalculatedTag[],
        ai_review_prompt: params.ai_review_prompt as string,
        ai_review_input_step_ids: params.ai_review_input_step_ids as string[],
        ai_assign_case_prompt: params.ai_assign_case_prompt as string,
        ai_assign_case_input_step_ids: params.ai_assign_case_input_step_ids as string[],
        process_stage_id: params.process_stage_id as ProcessStageId,
      })

      return {
        stepId: params.step_id,
        updates: { set_status: params.set_status },
        response,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editStatusBlock = createAsyncThunk(
  'processEdit/editStatusBlock',
  async (params: { block_id: BlockId; status: CaseStatusEnum }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      if (!currentProcess?.owner?.id) {
        return rejectWithValue('Process owner ID is required but not found in state')
      }

      const response = await apiZodios.cases_editStatusBlock({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        block_id: params.block_id,
        status: params.status,
      })

      return {
        blockId: params.block_id,
        status: response.status,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editTagBlock = createAsyncThunk(
  'processEdit/editTagBlock',
  async (
    params: Omit<EditTagBlockViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      if (!currentProcess?.owner?.id) {
        return rejectWithValue('Process owner ID is required but not found in state')
      }

      const response = await apiZodios.cases_editTagBlock({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        block_id: params.block_id,
        ...(params.tag_id ? { tag_id: params.tag_id } : {}),
        ...('apply_id' in params ? { apply_id: params.apply_id } : {}),
        ...(params.action ? { action: params.action } : {}),
        ...(params.applies_to ? { applies_to: params.applies_to } : {}),
        ...('identity_expression' in params
          ? { identity_expression: params.identity_expression }
          : {}),
      })

      return {
        blockId: params.block_id,
        tagAutomation: response.tag_automation,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

// TODO: wire up editRoleBlock dispatch from CaseRoleNode or an editing panel
export const editRoleBlock = createAsyncThunk(
  'processEdit/editRoleBlock',
  async (
    params: Omit<EditRoleBlockViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      if (!currentProcess?.owner?.id) {
        return rejectWithValue('Process owner ID is required but not found in state')
      }

      const response = await apiZodios.cases_editRoleBlock({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        block_id: params.block_id,
        ...(params.case_role_action ? { case_role_action: params.case_role_action } : {}),
        ...(params.identity_expression ? { identity_expression: params.identity_expression } : {}),
      })

      return {
        blockId: params.block_id,
        roleAutomation: response.case_role_automation,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editBlock = createAsyncThunk(
  'processEdit/editBlock',
  async (
    params: {
      block_id: BlockId
      process_stage_id?: ProcessStageId | null
      gate_type?: 'all_inbound' | 'any_inbound' | null
      entry_criteria?: ExpressionId | null
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const currentProcess = state.processEdit.currentProcess

      if (!userId) {
        return rejectWithValue('User ID is required but not found in state')
      }

      if (!currentProcess?.owner?.id) {
        return rejectWithValue('Process owner ID is required but not found in state')
      }

      const response = await apiZodios.cases_editBlock({
        user_id: userId,
        organization_identity_id: currentProcess.owner.id,
        block_id: params.block_id,
        ...(params.process_stage_id !== undefined
          ? { process_stage_id: params.process_stage_id }
          : {}),
        ...(params.gate_type != null ? { gate_type: params.gate_type } : {}),
        ...(params.entry_criteria !== undefined ? { entry_criteria: params.entry_criteria } : {}),
      })

      return {
        blockId: params.block_id,
        block: response.block,
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const deleteStepAssignment = createAsyncThunk(
  'processEdit/deleteStepAssignment',
  async (
    params: Omit<DeleteStepAssignmentViewRequestData, 'user_id' | 'organization_identity_id'>,
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID and Organization ID are required but not found in state')
      }

      const response = await apiZodios.cases_deleteStepAssignment({
        user_id: userId,
        organization_identity_id: processOwnerId as IdentityId,
        block_id: params.block_id as string,
        user_expression_id: params.user_expression_id as ExpressionId,
      })

      return response
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createConnector = createAsyncThunk(
  'processEdit/createConnector',
  async (
    params: {
      fromBlock: BlockId | null
      toBlock: BlockId | null
      temporaryId?: string
      forceReplay?: boolean
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID and Organization ID are required but not found in state')
      }

      const response = await apiZodios.cases_createConnector({
        user_id: userId,
        organization_identity_id: processOwnerId,
        from_block: params.fromBlock,
        to_block: params.toBlock,
        ...(params.forceReplay !== undefined ? { force_replay: params.forceReplay } : {}),
      })

      return {
        connector: response.connector,
        temporaryId: params.temporaryId,
        gateExpressions: response.gate_expressions,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const editConnector = createAsyncThunk(
  'processEdit/editConnector',
  async (
    params: {
      connectorId: ConnectorId
      fromBlock: BlockId | null
      toBlock: BlockId | null
      gate?: ExpressionId | null
      reviewOutcome?: ReviewOutcome
      forceReplay?: boolean
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID and Organization ID are required but not found in state')
      }

      const response = await apiZodios.cases_editConnector({
        user_id: userId,
        connector: params.connectorId,
        organization_identity_id: processOwnerId,
        from_block: params.fromBlock,
        to_block: params.toBlock,
        ...(params.gate !== undefined ? { gate: params.gate } : {}),
        ...(params.reviewOutcome ? { review_outcome: params.reviewOutcome } : {}),
        ...(params.forceReplay !== undefined ? { force_replay: params.forceReplay } : {}),
      })

      return {
        oldConnectorId: params.connectorId,
        connector: response.connector,
        gateExpressions: response.gate_expressions,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const deleteConnector = createAsyncThunk(
  'processEdit/deleteConnector',
  async (params: { connectorId: ConnectorId }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID and Organization ID are required but not found in state')
      }

      await apiZodios.cases_deleteConnector({
        user_id: userId,
        organization_identity_id: processOwnerId,
        connector: params.connectorId,
      })

      return {
        connectorId: params.connectorId,
      }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createFormBlock = createAsyncThunk(
  'processEdit/createFormBlock',
  async (params: { temporaryId: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const process = state.processEdit.currentProcess

      if (!userId || !process) {
        return rejectWithValue('User ID and Process are required but not found in state')
      }

      const { block, form_step: formStep } = await apiZodios.cases_createFormBlock({
        user_id: userId,
        organization_identity_id: process.owner.id,
        workflow_version_id: process.id,
      })

      return { block, formStep, temporaryId: params.temporaryId }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createPaymentBlock = createAsyncThunk(
  'processEdit/createPaymentBlock',
  async (params: { temporaryId: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const process = state.processEdit.currentProcess

      if (!userId || !process) {
        return rejectWithValue('User ID and Process are required but not found in state')
      }

      const { block, bill_step: billStep } = await apiZodios.cases_createPaymentBlock({
        user_id: userId,
        organization_identity_id: process.owner.id,
        workflow_version_id: process.id,
      })

      return { block, billStep, temporaryId: params.temporaryId }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const createStatusBlock = createAsyncThunk(
  'processEdit/createStatusBlock',
  // TODO: Add initialStatus param once backend supports it
  // async (params: { temporaryId: string; initialStatus?: string | null }, ...)
  async (params: { temporaryId: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const process = state.processEdit.currentProcess

      if (!userId || !process) {
        return rejectWithValue('User ID and Process are required but not found in state')
      }

      const { block, status } = await apiZodios.cases_createStatusBlock({
        user_id: userId,
        organization_identity_id: process.owner.id,
        workflow_version_id: process.id,
      })

      // TODO: Pass initialStatus through once backend supports it
      return { block, status, temporaryId: params.temporaryId }
    } catch (error) {
      console.error(error)
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

export const deleteBlock = createAsyncThunk(
  'processEdit/deleteBlock',
  async (params: { blockId: BlockId }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { processEdit: ProcessEditState }
      const userId = state.processEdit.userId
      const processOwnerId = state.processEdit.currentProcess?.owner.id

      if (!userId || !processOwnerId) {
        return rejectWithValue('User ID and Organization ID are required but not found in state')
      }

      await apiZodios.cases_deleteBlock({
        user_id: userId,
        organization_identity_id: processOwnerId,
        block_id: params.blockId,
      })

      return {
        blockId: params.blockId,
      }
    } catch (error) {
      console.error(error)
      if (axios.isAxiosError(error) && typeof error.response?.data?.detail === 'string') {
        return rejectWithValue({ detail: error.response.data.detail })
      }
      return rejectWithValue(error instanceof Error ? error.message : 'An unknown error occurred')
    }
  },
)

const cleanupBlockFlowState = (state: ProcessEditState, blockId: string): void => {
  state.flowBlocks = state.flowBlocks.filter((b) => b.id !== blockId)
  const reviewBlockId = state.flowBlockConfigurations?.review_blocks[blockId]?.id
  state.flowConnectors = state.flowConnectors.map((c) => {
    const gate = c.gate ? state.expressions[c.gate as ExpressionId] : undefined
    const isOwnOutcomeGate =
      Boolean(reviewBlockId) &&
      gate?.operator === OperatorType.enum.equals &&
      [gate.parameter_one, gate.parameter_two].some(
        (operandId) =>
          operandId &&
          state.expressions[operandId as ExpressionId]?.review_outcome === reviewBlockId,
      )
    return c.from_block === blockId
      ? { ...c, from_block: null, gate: isOwnOutcomeGate ? null : c.gate }
      : c
  })
  state.flowConnectors = state.flowConnectors.map((c) =>
    c.to_block === blockId ? { ...c, to_block: null } : c,
  )
  state.flowConnectors = state.flowConnectors.filter(
    (c) => c.from_block !== null || c.to_block !== null,
  )
  delete state.blockJoinTypes[blockId]
  delete state.blockJoinExpressionIds[blockId]
}

const cleanupCommunicationBlockState = (state: ProcessEditState, blockId: string): void => {
  cleanupBlockFlowState(state, blockId)
  if (state.currentProcess) {
    state.currentProcess = {
      ...state.currentProcess,
      blocks: state.currentProcess.blocks.filter((b) => b.id !== blockId),
    }
  }
  if (state.communicationAutomations[blockId]) {
    const next = { ...state.communicationAutomations }
    delete next[blockId]
    state.communicationAutomations = next
  }
  const config = state.flowBlockConfigurations
  if (config?.communication_automations[blockId]) {
    const next = { ...config.communication_automations }
    delete next[blockId]
    state.flowBlockConfigurations = {
      ...config,
      communication_automations: next,
    }
  }
  if (state.selectedCommunicationBlock === (blockId as BlockId)) {
    state.selectedCommunicationBlock = null
  }
  if (state.selectedRedactionBlock === (blockId as BlockId)) {
    state.selectedRedactionBlock = null
  }
}

const processEditSlice = createSlice({
  name: 'processEdit',
  initialState,
  reducers: {
    setCurrentProcess: (state, action: PayloadAction<{ process: Process3 }>) => {
      state.currentProcess = action.payload.process
    },
    setFlowIssues: (state, action: PayloadAction<FlowIssue[]>) => {
      if (JSON.stringify(state.flowIssues) !== JSON.stringify(action.payload)) {
        state.flowIssues = action.payload
      }
    },
    setSelectedStage: (state, action: PayloadAction<boolean>) => {
      state.selectedStage = action.payload
      state.selectedBlockCheckboxes = []
      if (action.payload) {
        state.selectedCommunicationBlock = null
        state.selectedRedactionBlock = null
      }
    },
    setSelectedStep: (state, action: PayloadAction<{ selectedStep: SelectedStep | null }>) => {
      state.selectedStep = action.payload.selectedStep
      state.selectedStage = false
      state.selectedBlockCheckboxes = []
      if (action.payload.selectedStep) {
        state.selectedCommunicationBlock = null
        state.selectedRedactionBlock = null
      }
    },
    setSelectedStepCheckboxes: (
      state,
      action: PayloadAction<{ selectedStepCheckboxes: StepId[] }>,
    ) => {
      state.selectedStepCheckboxes = action.payload.selectedStepCheckboxes
    },
    setSelectedBlockCheckboxes: (
      state,
      action: PayloadAction<{ selectedBlockCheckboxes: BlockId[] }>,
    ) => {
      state.selectedBlockCheckboxes = action.payload.selectedBlockCheckboxes
    },
    updateBlockStageLocally: (
      state,
      action: PayloadAction<{ blockId: BlockId; processStageId: ProcessStageId | null }>,
    ) => {
      const { blockId, processStageId } = action.payload
      state.flowBlocks = state.flowBlocks.map((b) =>
        b.id === (blockId as string) ? { ...b, process_stage: processStageId } : b,
      )
    },
    setSelectedFormSubstep: (
      state,
      action: PayloadAction<{ selectedSubstep: SelectedSubstep | null }>,
    ) => {
      state.selectedFormSubstep = action.payload.selectedSubstep
      state.selectedStage = false
      state.selectedCommunicationBlock = null
      state.selectedRedactionBlock = null
    },
    setSelectedCommunicationBlock: (state, action: PayloadAction<{ blockId: BlockId | null }>) => {
      state.selectedCommunicationBlock = action.payload.blockId
      if (action.payload.blockId) {
        state.selectedStep = null
        state.selectedFormSubstep = null
        state.selectedStage = false
      }
    },
    setCommunicationAutomations: (
      state,
      action: PayloadAction<{
        communicationAutomations: Record<string, CommunicationAutomation>
      }>,
    ) => {
      state.communicationAutomations = action.payload.communicationAutomations
    },
    setEmailCapability: (
      state,
      action: PayloadAction<{ emailCapability: CommunicationSourceEmails | null }>,
    ) => {
      state.emailCapability = action.payload.emailCapability
    },
    updateCommunicationBlockLocally: (
      state,
      action: PayloadAction<{
        blockId: string
        updates: Partial<CommunicationAutomation>
      }>,
    ) => {
      const { blockId, updates } = action.payload
      const existing = state.communicationAutomations[blockId]
      if (existing) {
        state.communicationAutomations = {
          ...state.communicationAutomations,
          [blockId]: { ...existing, ...updates },
        }
      }
      if (state.flowBlockConfigurations?.communication_automations[blockId]) {
        state.flowBlockConfigurations = {
          ...state.flowBlockConfigurations,
          communication_automations: {
            ...state.flowBlockConfigurations.communication_automations,
            [blockId]: {
              ...state.flowBlockConfigurations.communication_automations[blockId],
              ...updates,
            },
          },
        }
      }
    },
    updateDocumentStepLocally: (
      state,
      action: PayloadAction<{ blockId: string; updates: Partial<DocumentStep> }>,
    ) => {
      const { blockId, updates } = action.payload
      const existing = state.flowBlockConfigurations?.document_steps[blockId]
      if (!state.flowBlockConfigurations || !existing) return
      state.flowBlockConfigurations = {
        ...state.flowBlockConfigurations,
        document_steps: {
          ...state.flowBlockConfigurations.document_steps,
          [blockId]: { ...existing, ...updates },
        },
      }
    },
    removeCommunicationBlockLocally: (state, action: PayloadAction<{ blockId: string }>) => {
      cleanupCommunicationBlockState(state, action.payload.blockId)
    },
    setHasTrackerStageError: (state, action: PayloadAction<boolean>) => {
      state.hasTrackerStageError = action.payload
    },
    setTrackerStageErrorMessage: (state, action: PayloadAction<string>) => {
      state.trackerStageErrorMessage = action.payload
    },
    setUserId: (state, action: PayloadAction<{ userId: string | null }>) => {
      state.userId = action.payload.userId
    },
    addExpressionsLocally: (state, action: PayloadAction<{ expressions: Expression[] }>) => {
      const { expressions } = action.payload
      const newExpressions = {
        ...state.expressions,
        ...expressions.reduce<Record<string, Expression>>((acc, expression) => {
          acc[expression.id] = expression
          return acc
        }, {}),
      }
      state.expressions = newExpressions
    },
    editExpressionLocally: (
      state,
      action: PayloadAction<{
        expressionId: ExpressionId
        name?: string
        constant?: ValueId
        inputValue?: InputStepId
      }>,
    ) => {
      const { expressionId, name, constant, inputValue } = action.payload
      if (state.expressions[expressionId]) {
        state.expressions = {
          ...state.expressions,
          [expressionId]: {
            ...state.expressions[expressionId],
            ...(name !== undefined && { name }),
            ...(constant !== undefined && { constant }),
            ...(inputValue !== undefined && { input_value: inputValue }),
          },
        }
      }
    },
    deleteExpressionsLocally: (state, action: PayloadAction<{ expressionIds: ExpressionId[] }>) => {
      const { expressionIds } = action.payload
      const newExpressions = { ...state.expressions }
      expressionIds.forEach((id) => {
        delete newExpressions[id]
      })
      state.expressions = newExpressions
    },
    setExpressions: (
      state,
      action: PayloadAction<{ expressions: Record<ExpressionId, Expression> }>,
    ) => {
      state.expressions = action.payload.expressions
    },
    setModal: (state, action: PayloadAction<{ modal: ModalType | null }>) => {
      state.modal = action.payload.modal
    },
    setStepNotifications: (
      state,
      action: PayloadAction<{ stepNotifications: Record<BlockId, StepNotification[]> }>,
    ) => {
      state.stepNotifications = action.payload.stepNotifications
    },
    setSelectedRedactionBlock: (state, action: PayloadAction<{ blockId: BlockId | null }>) => {
      state.selectedRedactionBlock = action.payload.blockId
      if (action.payload.blockId) {
        state.selectedStep = null
        state.selectedFormSubstep = null
        state.selectedStage = false
        state.selectedCommunicationBlock = null
      }
    },
    setSnackbarConfiguration: (state, action: PayloadAction<SnackbarConfiguration>) => {
      state.snackbarConfiguration = action.payload
    },
    clearProcessEdit: (state) => {
      state.currentProcess = null
      state.selectedStep = null
      state.selectedFormSubstep = null
      state.selectedStage = false
      state.selectedBlockCheckboxes = []
      state.flowBlocks = []
      state.flowConnectors = []
      state.flowBlockConfigurations = null
      state.communicationAutomations = {}
      state.selectedCommunicationBlock = null
      state.selectedRedactionBlock = null
      state.emailCapability = null
      state.blockJoinTypes = {}
      state.blockJoinExpressionIds = {}
    },

    setBlockJoinTypeLocally: (
      state,
      action: PayloadAction<{ blockId: string; joinType: BlockJoinType }>,
    ) => {
      state.blockJoinTypes[action.payload.blockId] = action.payload.joinType
      if (action.payload.joinType !== 'custom') {
        delete state.blockJoinExpressionIds[action.payload.blockId]
      }
    },

    setBlockJoinExpressionLocally: (
      state,
      action: PayloadAction<{
        blockId: string
        rootExpressionId: ExpressionId
        expressions: Expression[]
      }>,
    ) => {
      const { blockId, rootExpressionId, expressions } = action.payload
      state.expressions = {
        ...state.expressions,
        ...expressions.reduce<Record<string, Expression>>((acc, expression) => {
          acc[expression.id] = expression
          return acc
        }, {}),
      }
      state.blockJoinTypes[blockId] = 'custom'
      state.blockJoinExpressionIds[blockId] = rootExpressionId
    },

    setFlowData: (
      state,
      action: PayloadAction<{
        blocks: Block[]
        connectors: Connector[]
        blockConfigurations: BlockConfigurations
      }>,
    ) => {
      state.flowBlocks = action.payload.blocks
      state.flowConnectors = action.payload.connectors
      state.flowBlockConfigurations = action.payload.blockConfigurations
      // Fresh server data is authoritative; drop optimistic join-type overrides
      // so a reload (incl. error-recovery reloads) reflects persisted gate_type
      // / entry_criteria rather than a stale local choice.
      state.blockJoinTypes = {}
      state.blockJoinExpressionIds = {}
    },

    createBlockLocally: (
      state,
      action: PayloadAction<{
        temporaryId: string
        blockType:
          | 'form'
          | 'payment'
          | 'status'
          | 'tag'
          | 'role'
          | 'communication'
          | 'document_generation'
          | 'license'
          | 'demo'
          | 'api_automation'
          | 'redaction'
          | 'review'
      }>,
    ) => {
      const { temporaryId, blockType } = action.payload
      const processId = state.currentProcess?.id ?? ''
      state.flowBlocks.push({
        id: temporaryId,
        process_version: processId,
        type: blockType,
        control_flow_replay:
          blockType === 'license'
            ? 'never'
            : blockType === 'payment' || blockType === 'api_automation' || blockType === 'redaction'
              ? 'confirm'
              : 'always',
        entry_criteria: null,
        process_stage: null,
        metadata: null,
      } as Block)
      if (blockType === 'status' && state.flowBlockConfigurations) {
        state.flowBlockConfigurations.status_automations[temporaryId] = {
          id: temporaryId,
          block: temporaryId,
          status: 'Open',
        }
      }
      if (blockType === 'tag' && state.flowBlockConfigurations) {
        state.flowBlockConfigurations.tag_automations[temporaryId] = {
          id: temporaryId,
          block: temporaryId,
          tag: null,
          apply: null,
          metadata: null,
          action: 'add',
        }
      }
      if (blockType === 'role' && state.flowBlockConfigurations) {
        state.flowBlockConfigurations.role_automations[temporaryId] = {
          id: temporaryId,
          block: temporaryId,
          identity_expression: null,
          metadata: null,
          case_role_action: 'set_requester',
        }
      }
      if (blockType === 'communication' && state.flowBlockConfigurations) {
        state.flowBlockConfigurations.communication_automations[temporaryId] = {
          id: temporaryId,
          block: temporaryId,
          body: '',
          name: 'Send Email',
          recipients: [],
        }
      }
      if (blockType === 'demo' && state.flowBlockConfigurations) {
        state.flowBlockConfigurations.demo_blocks[temporaryId] = {
          id: temporaryId,
          block: temporaryId,
          name: 'Example',
          description: '',
          icon: 'star',
          color: '',
          metadata: null,
        }
      }
      if (blockType === 'api_automation' && state.flowBlockConfigurations) {
        state.flowBlockConfigurations.api_automations[temporaryId] = {
          id: temporaryId,
          block: temporaryId,
          api_endpoint: null,
          input_mappings: [],
          metadata: null,
        }
      }
      if (blockType === 'review' && state.flowBlockConfigurations) {
        state.flowBlockConfigurations.review_blocks[temporaryId] = {
          id: temporaryId,
          block: temporaryId,
          description: '',
          allowed_outcomes: ['accept', 'reject'],
          automated_review_prompt: '',
          due: null,
          sla: null,
          focus_fields: [],
          reviewer_rulesets: [],
          metadata: null,
        }
      }
      if (blockType === 'license' && state.flowBlockConfigurations) {
        state.flowBlockConfigurations.license_blocks[temporaryId] = {
          id: temporaryId,
          block: temporaryId,
          license_template: null,
          metadata: null,
          application_type: 'new',
        }
      }
      if (blockType === 'redaction' && state.flowBlockConfigurations) {
        state.flowBlockConfigurations.redaction_blocks[temporaryId] = {
          id: temporaryId,
          block: temporaryId,
          name: 'Redaction',
          metadata: null,
        }
      }
    },

    updateRedactionBlockLocally: (
      state,
      action: PayloadAction<{ blockId: BlockId; name: string }>,
    ) => {
      const { blockId, name } = action.payload
      if (state.flowBlockConfigurations?.redaction_blocks[blockId]) {
        state.flowBlockConfigurations.redaction_blocks[blockId] = {
          ...state.flowBlockConfigurations.redaction_blocks[blockId],
          name,
        }
      }
    },

    updateDemoBlockLocally: (
      state,
      action: PayloadAction<{
        blockId: string
        name?: string
        description?: string
        icon?: string
        color?: string
      }>,
    ) => {
      const { blockId, name, description, icon, color } = action.payload
      if (state.flowBlockConfigurations?.demo_blocks[blockId]) {
        state.flowBlockConfigurations.demo_blocks[blockId] = {
          ...state.flowBlockConfigurations.demo_blocks[blockId],
          ...(name !== undefined ? { name } : {}),
          ...(description !== undefined ? { description } : {}),
          ...(icon !== undefined ? { icon } : {}),
          ...(color !== undefined ? { color } : {}),
        }
      }
    },

    updateLicenseBlockLocally: (
      state,
      action: PayloadAction<{
        blockId: string
        licenseTemplateId?: LicenseTemplateId | null
        applicationType?: LicenseApplicationType
      }>,
    ) => {
      const { blockId, licenseTemplateId, applicationType } = action.payload
      if (state.flowBlockConfigurations?.license_blocks[blockId]) {
        state.flowBlockConfigurations.license_blocks[blockId] = {
          ...state.flowBlockConfigurations.license_blocks[blockId],
          ...(licenseTemplateId !== undefined ? { license_template: licenseTemplateId } : {}),
          ...(applicationType !== undefined ? { application_type: applicationType } : {}),
        }
      }
    },

    deleteBlockLocally: (state, action: PayloadAction<{ blockId: string }>) => {
      const { blockId } = action.payload
      state.flowBlocks = state.flowBlocks.filter((b) => b.id !== blockId)
      // Outgoing connectors become dangling (SET_NULL) → ghost source nodes appear
      state.flowConnectors = state.flowConnectors.map((c) =>
        c.from_block === blockId ? { ...c, from_block: null } : c,
      )
      // Incoming connectors become dangling (SET_NULL) → ghost target nodes appear
      state.flowConnectors = state.flowConnectors.map((c) =>
        c.to_block === blockId ? { ...c, to_block: null } : c,
      )
      // Remove connectors that now have both ends null (orphaned)
      state.flowConnectors = state.flowConnectors.filter(
        (c) => c.from_block !== null || c.to_block !== null,
      )
      delete state.blockJoinTypes[blockId]
      delete state.blockJoinExpressionIds[blockId]
      if (state.flowBlockConfigurations) {
        const deletedFormStep = state.flowBlockConfigurations.form_steps[blockId]
        const deletedBillStep = state.flowBlockConfigurations.bill_steps[blockId]
        const newFormSteps = { ...state.flowBlockConfigurations.form_steps }
        const newBillSteps = { ...state.flowBlockConfigurations.bill_steps }
        const newStatusAutomations = { ...state.flowBlockConfigurations.status_automations }
        const newTagAutomations = { ...state.flowBlockConfigurations.tag_automations }
        const newRoleAutomations = { ...state.flowBlockConfigurations.role_automations }
        const newCommunicationAutomations = {
          ...state.flowBlockConfigurations.communication_automations,
        }
        const newDocumentSteps = { ...state.flowBlockConfigurations.document_steps }
        const newLicenseBlocks = { ...state.flowBlockConfigurations.license_blocks }
        const newDemoBlocks = { ...state.flowBlockConfigurations.demo_blocks }
        const newAPIAutomations = { ...state.flowBlockConfigurations.api_automations }
        const newRedactionBlocks = { ...state.flowBlockConfigurations.redaction_blocks }
        const newReviewBlocks = { ...state.flowBlockConfigurations.review_blocks }
        delete newDemoBlocks[blockId]
        delete newAPIAutomations[blockId]
        delete newRedactionBlocks[blockId]
        delete newReviewBlocks[blockId]
        delete newLicenseBlocks[blockId]
        delete newFormSteps[blockId]
        delete newBillSteps[blockId]
        delete newStatusAutomations[blockId]
        delete newTagAutomations[blockId]
        delete newRoleAutomations[blockId]
        delete newCommunicationAutomations[blockId]
        delete newDocumentSteps[blockId]
        state.flowBlockConfigurations = {
          ...state.flowBlockConfigurations,
          form_steps: newFormSteps,
          bill_steps: newBillSteps,
          status_automations: newStatusAutomations,
          tag_automations: newTagAutomations,
          role_automations: newRoleAutomations,
          communication_automations: newCommunicationAutomations,
          document_steps: newDocumentSteps,
          license_blocks: newLicenseBlocks,
          demo_blocks: newDemoBlocks,
          api_automations: newAPIAutomations,
          redaction_blocks: newRedactionBlocks,
          review_blocks: newReviewBlocks,
        }
        if (state.currentProcess) {
          if (deletedFormStep) {
            state.currentProcess = {
              ...state.currentProcess,
              form_steps: state.currentProcess.form_steps.filter(
                (fs) => fs.id !== deletedFormStep.id,
              ),
            }
          }
          if (deletedBillStep) {
            state.currentProcess = {
              ...state.currentProcess,
              bill_steps: state.currentProcess.bill_steps.filter(
                (bs) => bs.id !== deletedBillStep.id,
              ),
            }
          }
        }
      }
      if (state.selectedCommunicationBlock === (blockId as BlockId)) {
        state.selectedCommunicationBlock = null
      }
      if (state.selectedRedactionBlock === (blockId as BlockId)) {
        state.selectedRedactionBlock = null
      }
    },

    deleteConnectorLocally: (state, action: PayloadAction<{ connectorId: string }>) => {
      state.flowConnectors = state.flowConnectors.filter((c) => c.id !== action.payload.connectorId)
    },

    addConnectorLocally: (
      state,
      action: PayloadAction<{
        temporaryId: string
        fromBlock: string | null
        toBlock: string | null
        forceReplay?: boolean
      }>,
    ) => {
      const { temporaryId, fromBlock, toBlock, forceReplay } = action.payload
      state.flowConnectors.push({
        id: temporaryId,
        from_block: fromBlock,
        to_block: toBlock,
        gate: null,
        metadata: null,
        force_replay: forceReplay ?? false,
      } as Connector)
    },

    updateConnectorLocally: (
      state,
      action: PayloadAction<{
        connectorId: string
        fromBlock?: string | null
        toBlock?: string | null
        gate?: string | null
        forceReplay?: boolean
      }>,
    ) => {
      const { connectorId, fromBlock, toBlock, gate, forceReplay } = action.payload
      const connector = state.flowConnectors.find((c) => c.id === connectorId)
      if (connector) {
        if (fromBlock !== undefined) connector.from_block = fromBlock
        if (toBlock !== undefined) connector.to_block = toBlock
        if (gate !== undefined) connector.gate = gate
        if (forceReplay !== undefined) connector.force_replay = forceReplay
      }
    },

    createProcessStageLocally: (
      state,
      action: PayloadAction<{ process_id: string; order: number } & { temporaryId: string }>,
    ) => {
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          process_stages: [
            ...state.currentProcess.process_stages,
            {
              id: action.payload.temporaryId,
              process: action.payload.process_id as ProcessId,
              name: '',
              description: '',
              order: action.payload.order,
              created: '',
              updated: '',
            },
          ],
        }
      }
    },
    createInputStepLocally: (
      state,
      action: PayloadAction<
        Omit<CreateInputStepViewRequestData, 'user_id' | 'organization_identity_id'> & {
          temporaryId: string
        }
      >,
    ) => {
      if (state.currentProcess) {
        let substep = { type: action.payload.value_type }
        if (action.payload.value_type === ValueTypeEnum.enum.identity) {
          substep = {
            type: action.payload.value_type,
            identity_step_sub: {
              id: action.payload.temporaryId,
              input_step: action.payload.temporaryId,
              type: ValueTypeEnum.enum.identity,
              require_me: true,
              show_address: true,
              require_address: false,
              show_date_of_birth: false,
              require_date_of_birth: false,
              show_email: true,
              require_email: true,
              verify_email: false,
              show_name: true,
              require_name: false,
              show_phone: true,
              require_phone: false,
              add_contact: false,
            },
          } as InputStepSub0
        } else if (action.payload.value_type === ValueTypeEnum.enum.datetime) {
          substep = {
            type: action.payload.value_type,
            datetime_step_sub: {
              id: action.payload.temporaryId,
              input_step: action.payload.temporaryId,
              type: ValueTypeEnum.enum.datetime,
              request_time: false,
            },
          } as InputStepSub0
        } else if (action.payload.value_type === ValueTypeEnum.enum.file) {
          substep = {
            type: action.payload.value_type,
            file_step_sub: {
              id: action.payload.temporaryId,
              input_step: action.payload.temporaryId,
              type: ValueTypeEnum.enum.file,
              allowed_file_types: null,
            },
          } as InputStepSub0
        } else if (action.payload.value_type === ValueTypeEnum.enum.number) {
          substep = {
            type: action.payload.value_type,
            number_step_sub: {
              id: action.payload.temporaryId,
              input_step: action.payload.temporaryId,
              type: ValueTypeEnum.enum.number,
              allow_negative_numbers: false,
              allow_zero: false,
              decimal_places: 0,
              prefix_unit: null,
              suffix_unit: null,
            },
          } as InputStepSub0
        } else if (action.payload.value_type === ValueTypeEnum.enum.location) {
          substep = {
            type: action.payload.value_type,
            location_step_sub: {
              id: action.payload.temporaryId,
              input_step: action.payload.temporaryId,
              type: ValueTypeEnum.enum.location,
              request_address: true,
              request_coordinates: true,
            },
          } as InputStepSub0
        }
        state.currentProcess = {
          ...state.currentProcess,
          inputs: [
            ...state.currentProcess.inputs,
            {
              id: action.payload.temporaryId,
              sub: substep as InputStepSub0,
              choices: [],
              template: action.payload.template as ValueId | null,
              form: action.payload.form as FormStepId,
              type: action.payload.value_type as ValueType,
              process: state.currentProcess.id,
              code: '',
              name: action.payload.name,
              description: action.payload.description,
              order: action.payload.order,
              style: action.payload.style,
              section: action.payload.section,
              required: action.payload.required,
              external: false,
              sla: null,
              due: null,
              pinned: action.payload.pinned,
              added_at: null,
              removed_at: null,
              calculated_value: null,
            } as InputSubstep2,
          ],
        }
      }
    },
    createSectionStepLocally: (
      state,
      action: PayloadAction<
        Omit<CreateSectionStepViewRequestData, 'user_id' | 'organization_identity_id'> & {
          temporaryId: string
        }
      >,
    ) => {
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          subsections: [
            ...state.currentProcess.subsections,
            {
              id: action.payload.temporaryId,
              process: state.currentProcess.id as ProcessId,
              name: action.payload.name,
              description: action.payload.description,
              order: action.payload.order,
              form: action.payload.form as FormStepId,
              section: action.payload.section as SectionStepId | null,
              button_name: action.payload.button_name,
              added_at: null,
              removed_at: null,
              multiple: (action.payload.max as number) > 1,
              min: action.payload.min,
              max: action.payload.max,
              render: action.payload.render as ExpressionId | null,
            } as SectionSubstep0,
          ],
        }
      }
    },
    createFormStyleElementLocally: (
      state,
      action: PayloadAction<
        Omit<CreateFormStyleElementViewRequestData, 'user_id' | 'organization_identity_id'> & {
          temporary_id: string
        }
      >,
    ) => {
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          page_breaks: [
            ...state.currentProcess.page_breaks,
            {
              id: action.payload.temporary_id,
              form_step: action.payload.form_step_id,
              type: action.payload.type,
              order: action.payload.order,
            } as FormStyleElement,
          ],
        }
      }
    },
    copySectionStepLocally: (
      state,
      action: PayloadAction<{
        orderedSections: Array<
          Omit<CreateSectionStepViewRequestData, 'user_id' | 'organization_identity_id'> & {
            previous_id: SectionStepId
          }
        >
        orderedInputs: Array<
          Omit<CreateInputStepViewRequestData, 'user_id'> & {
            previous_id: InputStepId
          }
        >
      }>,
    ) => {
      if (state.currentProcess) {
        const copiedInputs = action.payload.orderedInputs.map((newInput) => {
          const newTempId = `${newInput.previous_id}-copy`
          let substep = { type: newInput.value_type }
          if (newInput.value_type === ValueTypeEnum.enum.identity) {
            substep = {
              type: newInput.value_type,
              identity_step_sub: {
                id: newTempId,
                input_step: newTempId,
                type: ValueTypeEnum.enum.identity,
                require_me: true,
                show_address: true,
                require_address: false,
                show_date_of_birth: false,
                require_date_of_birth: false,
                show_email: true,
                require_email: true,
                verify_email: false,
                show_name: true,
                require_name: false,
                show_phone: true,
                require_phone: false,
                add_contact: false,
              },
            } as InputStepSub0
          } else if (newInput.value_type === ValueTypeEnum.enum.datetime) {
            substep = {
              type: newInput.value_type,
              datetime_step_sub: {
                id: newTempId,
                input_step: newTempId,
                type: ValueTypeEnum.enum.datetime,
                request_time: false,
              },
            } as InputStepSub0
          }
          return {
            id: newTempId,
            sub: substep as InputStepSub0,
            choices: [],
            template: newInput.template as ValueId | null,
            form: newInput.form as FormStepId,
            type: newInput.value_type as ValueType,
            process: state.currentProcess?.id,
            code: '',
            name: newInput.name,
            description: newInput.description,
            order: newInput.order,
            style: newInput.style,
            section: newInput.section,
            required: newInput.required,
            external: false,
            sla: null,
            due: null,
            pinned: newInput.pinned,
            added_at: null,
            removed_at: null,
            calculated_value: null,
          } as InputSubstep2
        })
        const copiedSections = action.payload.orderedSections.map((newSection) => {
          return {
            id: `${newSection.previous_id}-copy`,
            process: state.currentProcess?.id as ProcessId,
            name: newSection.name,
            description: newSection.description,
            order: newSection.order,
            form: newSection.form as FormStepId,
            section: newSection.section as SectionStepId | null,
            button_name: newSection.button_name,
            added_at: null,
            removed_at: null,
            multiple: (newSection.max as number) > 1,
            min: newSection.min,
            max: newSection.max,
            render: newSection.render,
          } as SectionSubstep0
        })
        state.currentProcess = {
          ...state.currentProcess,
          inputs: [...state.currentProcess.inputs, ...copiedInputs],
          subsections: [...state.currentProcess.subsections, ...copiedSections],
        }
      }
    },
    deleteInputStepLocally: (
      state,
      action: PayloadAction<
        Omit<DeleteInputStepViewRequestData, 'user_id' | 'organization_identity_id'>
      >,
    ) => {
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          inputs: state.currentProcess.inputs.filter(
            (input) => input.id !== action.payload.input_step_id,
          ),
        }
      }
    },
    deleteSectionStepLocally: (
      state,
      action: PayloadAction<
        Omit<DeleteSectionSubstepViewRequestData, 'user_id' | 'organization_identity_id'>
      >,
    ) => {
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          subsections: state.currentProcess.subsections.filter(
            (subsection) => subsection.id !== action.payload.section_substep_id,
          ),
        }
      }
    },
    deleteFormStyleElementLocally: (
      state,
      action: PayloadAction<
        Omit<DeleteFormStyleElementViewRequestData, 'user_id' | 'organization_identity_id'>
      >,
    ) => {
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          page_breaks: state.currentProcess.page_breaks.filter(
            (pageBreak) => pageBreak.id !== action.payload.form_style_element_id,
          ),
        }
      }
    },
    deleteStepLocally: (
      state,
      action: PayloadAction<{
        stepId: FormStepId | BillStepId
        type: StepType
      }>,
    ) => {
      if (action.payload.type === StepType.bill && state.currentProcess && action.payload.stepId) {
        state.currentProcess = {
          ...state.currentProcess,
          bill_steps: state.currentProcess.bill_steps.filter(
            (billStep) => billStep.id !== action.payload.stepId,
          ),
        }
      } else if (
        action.payload.type === StepType.form &&
        state.currentProcess &&
        action.payload.stepId
      ) {
        state.currentProcess = {
          ...state.currentProcess,
          form_steps: state.currentProcess.form_steps.filter(
            (formStep) => formStep.id !== action.payload.stepId,
          ),
        }
      }
    },
    deleteProcessStageLocally: (
      state,
      action: PayloadAction<{ process_stage_id: ProcessStageId }>,
    ) => {
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          process_stages: state.currentProcess.process_stages.filter(
            (processStage) => processStage.id !== action.payload.process_stage_id,
          ),
          form_steps: state.currentProcess.form_steps.map((formStep) =>
            formStep.process_stage === action.payload.process_stage_id
              ? { ...formStep, process_stage: null }
              : formStep,
          ),
          bill_steps: state.currentProcess.bill_steps.map((billStep) =>
            billStep.process_stage === action.payload.process_stage_id
              ? { ...billStep, process_stage: null }
              : billStep,
          ),
        }
      }
      state.flowBlocks = state.flowBlocks.map((b) =>
        b.process_stage === (action.payload.process_stage_id as string)
          ? { ...b, process_stage: null }
          : b,
      )
    },
    deleteStepAssignmentLocally: (
      state,
      action: PayloadAction<{ block_id: BlockId; user_expression_id: string }>,
    ) => {
      const { block_id: blockId, user_expression_id: userExpressionId } = action.payload
      const updatedStepNotifications = { ...state.stepNotifications }
      if (updatedStepNotifications[blockId]) {
        updatedStepNotifications[blockId] = updatedStepNotifications[blockId].filter(
          (notification) => notification.user !== userExpressionId,
        )
      }
      state.stepNotifications = updatedStepNotifications
    },
    updateBillStepLocally: (
      state,
      action: PayloadAction<
        Omit<EditBillStepViewRequestData, 'user_id' | 'organization_identity_id'>
      >,
    ) => {
      const { bill_step_id: billStepId, ...updates } = action.payload
      const chargeSubstepCreates = updates.charges as ChargeSubStepCreate[] | undefined

      if (state.currentProcess && state.currentProcess.bill_steps) {
        state.currentProcess = {
          ...state.currentProcess,
          bill_steps: state.currentProcess.bill_steps.map((billStep) => {
            if (billStep.id === billStepId) {
              let newCharges: ChargeSubstep[]
              if (chargeSubstepCreates) {
                newCharges = chargeSubstepCreates.map((charge) => ({
                  id: nanoid(),
                  bill: billStepId,
                  dollars: charge.dollars,
                  code: '',
                  name: charge.name,
                  description: charge.description,
                  dollars_type: ValueTypeEnum.enum.number,
                  account_receivable_code: charge.account_receivable_code ?? null,
                  distribution_code: charge.distribution_code ?? null,
                }))
              } else {
                newCharges = billStep.charges
              }
              return {
                ...billStep,
                name: updates.name ?? billStep.name,
                description: updates.description ?? billStep.description,
                order: updates.order ?? billStep.order,
                charges: newCharges,
                account: updates.account ?? billStep.account,
                distribution: updates.distribution ?? billStep.distribution,
              } as BillStep1
            }
            return billStep
          }),
        }
      }

      // Sync flowBlockConfigurations so flow nodes reflect the change
      if (state.flowBlockConfigurations) {
        const blockId = Object.keys(state.flowBlockConfigurations.bill_steps).find(
          (bId) => state.flowBlockConfigurations!.bill_steps[bId].id === billStepId,
        )
        if (blockId) {
          const bs = state.flowBlockConfigurations.bill_steps[blockId]
          state.flowBlockConfigurations.bill_steps[blockId] = {
            ...bs,
            name: updates.name ?? bs.name,
            description: updates.description ?? bs.description,
            installments:
              (updates as unknown as { installments?: number }).installments ?? bs.installments,
            account: updates.account !== undefined ? updates.account : bs.account,
            distribution:
              updates.distribution !== undefined ? updates.distribution : bs.distribution,
          }
        }
      }
    },
    updateFormStepLocally: (
      state,
      action: PayloadAction<
        Omit<EditFormStepViewRequestData, 'user_id' | 'organization_identity_id'>
      >,
    ) => {
      const { form_step_id: formStepId, ...updates } = action.payload

      // Update the step in the current process if it exists
      if (state.currentProcess && state.currentProcess.form_steps) {
        state.currentProcess = {
          ...state.currentProcess,
          form_steps: state.currentProcess.form_steps.map((formStep) => {
            if (formStep.id === formStepId) {
              return {
                ...formStep,
                // Map API field names to your local model field names if needed
                name: updates.name ?? formStep.name,
                description: updates.description ?? formStep.description,
                order: updates.order ?? formStep.order,
                external: updates.external ?? formStep.external,
                sla: updates.sla ?? formStep.sla,
                due: updates.due ?? formStep.due,
                button_name:
                  updates.button_name === undefined ? formStep.button_name : updates.button_name,
                requires_submission_confirmation:
                  updates.requires_submission_confirmation ??
                  formStep.requires_submission_confirmation,
              } as FormStep0
            }
            return formStep
          }),
        }
      }

      // Sync flowBlockConfigurations so flow nodes reflect the change
      if (state.flowBlockConfigurations) {
        const blockId = Object.keys(state.flowBlockConfigurations.form_steps).find(
          (bId) => state.flowBlockConfigurations!.form_steps[bId].id === formStepId,
        )
        if (blockId) {
          const fs = state.flowBlockConfigurations.form_steps[blockId]
          state.flowBlockConfigurations.form_steps[blockId] = {
            ...fs,
            name: updates.name ?? fs.name,
            description: updates.description ?? fs.description,
            external: updates.external ?? fs.external,
            due: updates.due !== undefined ? updates.due : fs.due,
            sla: updates.sla !== undefined ? updates.sla : fs.sla,
            button_name: updates.button_name !== undefined ? updates.button_name : fs.button_name,
            requires_submission_confirmation:
              updates.requires_submission_confirmation ?? fs.requires_submission_confirmation,
          }
        }
      }
    },
    updateStepLocally: (
      state,
      action: PayloadAction<Omit<EditStepViewRequestData, 'user_id' | 'organization_identity_id'>>,
    ) => {
      const {
        step_id: stepId,
        set_status: setStatus,
        calculated_tags: calculatedTags,
        ai_review_prompt: aiReviewPrompt,
        ai_review_input_step_ids: aiReviewInputStepIds,
        ai_assign_case_prompt: aiAssignCasePrompt,
        ai_assign_case_input_step_ids: aiAssignCaseInputStepIds,
        process_stage_id: processStageId,
      } = action.payload
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          form_steps: state.currentProcess.form_steps.map((formStep) => {
            if (formStep.step === stepId) {
              return {
                ...formStep,
                ...(setStatus !== undefined && { set_status: setStatus }),
                ...(calculatedTags !== undefined && { calculated_tags: calculatedTags }),
                ...(aiReviewPrompt !== undefined && { ai_review_prompt: aiReviewPrompt }),
                ...(aiReviewInputStepIds !== undefined && {
                  ai_review_input_step_ids: aiReviewInputStepIds,
                }),
                ...(aiAssignCasePrompt !== undefined && {
                  ai_assign_case_prompt: aiAssignCasePrompt,
                }),
                ...(aiAssignCaseInputStepIds !== undefined && {
                  ai_assign_case_input_step_ids: aiAssignCaseInputStepIds,
                }),
                ...(processStageId !== undefined && { process_stage: processStageId }),
              } as FormStep0
            }
            return formStep
          }),
          bill_steps: state.currentProcess.bill_steps.map((billStep) => {
            if (billStep.step === stepId) {
              return {
                ...billStep,
                ...(setStatus !== undefined && { set_status: setStatus }),
                ...(calculatedTags !== undefined && { calculated_tags: calculatedTags }),
                ...(processStageId !== undefined && { process_stage: processStageId }),
              } as BillStep1
            }
            return billStep
          }),
        }
      }

      if (setStatus !== undefined && state.flowBlockConfigurations) {
        const stepBlockId =
          Object.keys(state.flowBlockConfigurations.form_steps).find(
            (bId) => state.flowBlockConfigurations!.form_steps[bId].step === stepId,
          ) ??
          Object.keys(state.flowBlockConfigurations.bill_steps).find(
            (bId) => state.flowBlockConfigurations!.bill_steps[bId].step === stepId,
          )
        if (stepBlockId) {
          const connectorToStatus = state.flowConnectors.find(
            (c) =>
              c.from_block === stepBlockId &&
              c.to_block != null &&
              state.flowBlockConfigurations!.status_automations[c.to_block] != null,
          )
          const statusBlockId = connectorToStatus?.to_block ?? null

          if (setStatus === null && statusBlockId) {
            const connectorOut = state.flowConnectors.find((c) => c.from_block === statusBlockId)
            const afterBlockId = connectorOut?.to_block ?? null

            const removeIds = new Set(
              [connectorToStatus?.id, connectorOut?.id].filter(Boolean) as string[],
            )
            state.flowConnectors = state.flowConnectors.filter((c) => !removeIds.has(c.id))

            if (afterBlockId) {
              state.flowConnectors.push({
                id: `temp-connector-${Date.now()}`,
                from_block: stepBlockId,
                to_block: afterBlockId,
                gate: null,
                metadata: null,
              } as Connector)
            }

            state.flowBlocks = state.flowBlocks.filter((b) => b.id !== statusBlockId)
            const newStatusAutomations = { ...state.flowBlockConfigurations.status_automations }
            delete newStatusAutomations[statusBlockId]
            state.flowBlockConfigurations = {
              ...state.flowBlockConfigurations,
              status_automations: newStatusAutomations,
            }
          } else if (setStatus !== null && !statusBlockId) {
            const tempBlockId = `temp-status-${Date.now()}`
            const processId = state.currentProcess?.id ?? ''

            state.flowBlocks.push({
              id: tempBlockId,
              process_version: processId,
              type: 'status',
              control_flow_replay: 'always',
              entry_criteria: null,
              process_stage: null,
              metadata: null,
            } as Block)

            const existingConnector = state.flowConnectors.find((c) => c.from_block === stepBlockId)
            if (existingConnector) {
              const afterBlockId = existingConnector.to_block
              state.flowConnectors = state.flowConnectors.map((c) =>
                c.id === existingConnector.id ? { ...c, to_block: tempBlockId } : c,
              )
              if (afterBlockId) {
                state.flowConnectors.push({
                  id: `temp-connector-${Date.now()}`,
                  from_block: tempBlockId,
                  to_block: afterBlockId,
                  gate: null,
                  metadata: null,
                } as Connector)
              }
            } else {
              state.flowConnectors.push({
                id: `temp-connector-${Date.now()}`,
                from_block: stepBlockId,
                to_block: tempBlockId,
                gate: null,
                metadata: null,
              } as Connector)
            }

            state.flowBlockConfigurations.status_automations[tempBlockId] = {
              id: `temp-automation-${Date.now()}`,
              block: tempBlockId,
              status: setStatus,
              metadata: null,
            } as CaseStatusAutomation
          } else if (setStatus !== null && statusBlockId) {
            state.flowBlockConfigurations.status_automations[statusBlockId] = {
              ...state.flowBlockConfigurations.status_automations[statusBlockId],
              status: setStatus,
            }
          }
        }
      }
    },
    updateStatusBlockLocally: (
      state,
      action: PayloadAction<{ blockId: string; status: CaseStatusEnum }>,
    ) => {
      const { blockId, status } = action.payload
      if (state.flowBlockConfigurations?.status_automations[blockId]) {
        state.flowBlockConfigurations.status_automations[blockId] = {
          ...state.flowBlockConfigurations.status_automations[blockId],
          status,
        }
      }
    },
    updateTagBlockLocally: (
      state,
      action: PayloadAction<{
        blockId: string
        tagId?: string | null
        applyId?: string | null
        tagAction?: TagAutomationAction
        appliesTo?: TagAutomationTarget
        identityExpression?: ExpressionId | null
      }>,
    ) => {
      const { blockId, tagId, applyId, tagAction, appliesTo, identityExpression } = action.payload
      if (state.flowBlockConfigurations?.tag_automations[blockId]) {
        state.flowBlockConfigurations.tag_automations[blockId] = {
          ...state.flowBlockConfigurations.tag_automations[blockId],
          ...(tagId !== undefined ? { tag: tagId } : {}),
          ...(applyId !== undefined ? { apply: applyId } : {}),
          ...(tagAction !== undefined ? { action: tagAction } : {}),
          ...(appliesTo !== undefined ? { applies_to: appliesTo } : {}),
          ...(identityExpression !== undefined ? { identity_expression: identityExpression } : {}),
        }
      }
    },
    updateRoleBlockLocally: (
      state,
      action: PayloadAction<{
        blockId: string
        caseRoleAction?: CaseRoleAutomation['case_role_action']
        identityExpression?: string | null
      }>,
    ) => {
      const { blockId, caseRoleAction, identityExpression } = action.payload
      if (state.flowBlockConfigurations?.role_automations[blockId]) {
        state.flowBlockConfigurations.role_automations[blockId] = {
          ...state.flowBlockConfigurations.role_automations[blockId],
          ...(caseRoleAction !== undefined ? { case_role_action: caseRoleAction } : {}),
          ...(identityExpression !== undefined ? { identity_expression: identityExpression } : {}),
        }
      }
    },
    removeAutomationBlockLocally: (state, action: PayloadAction<{ blockId: string }>) => {
      const { blockId } = action.payload
      if (!state.flowBlockConfigurations) return

      cleanupBlockFlowState(state, blockId)

      const newStatusAutomations = { ...state.flowBlockConfigurations.status_automations }
      const newTagAutomations = { ...state.flowBlockConfigurations.tag_automations }
      const newRoleAutomations = { ...state.flowBlockConfigurations.role_automations }
      const newCommunicationAutomations = {
        ...state.flowBlockConfigurations.communication_automations,
      }
      const newDocumentSteps = { ...state.flowBlockConfigurations.document_steps }
      const newLicenseBlocks = { ...state.flowBlockConfigurations.license_blocks }
      const newDemoBlocks = { ...state.flowBlockConfigurations.demo_blocks }
      const newAPIAutomations = { ...state.flowBlockConfigurations.api_automations }
      const newRedactionBlocks = { ...state.flowBlockConfigurations.redaction_blocks }
      const newReviewBlocks = { ...state.flowBlockConfigurations.review_blocks }
      delete newDemoBlocks[blockId]
      delete newAPIAutomations[blockId]
      delete newRedactionBlocks[blockId]
      delete newReviewBlocks[blockId]
      delete newStatusAutomations[blockId]
      delete newTagAutomations[blockId]
      delete newRoleAutomations[blockId]
      delete newCommunicationAutomations[blockId]
      delete newDocumentSteps[blockId]
      delete newLicenseBlocks[blockId]
      state.flowBlockConfigurations = {
        ...state.flowBlockConfigurations,
        status_automations: newStatusAutomations,
        tag_automations: newTagAutomations,
        role_automations: newRoleAutomations,
        communication_automations: newCommunicationAutomations,
        document_steps: newDocumentSteps,
        license_blocks: newLicenseBlocks,
        demo_blocks: newDemoBlocks,
        api_automations: newAPIAutomations,
        redaction_blocks: newRedactionBlocks,
        review_blocks: newReviewBlocks,
      }
      if (state.selectedCommunicationBlock === (blockId as BlockId)) {
        state.selectedCommunicationBlock = null
      }
      if (state.selectedRedactionBlock === (blockId as BlockId)) {
        state.selectedRedactionBlock = null
      }
    },
    updateProcessStageLocally: (
      state,
      action: PayloadAction<
        Omit<EditProcessStageViewRequestData, 'user_id' | 'organization_identity_id'>
      >,
    ) => {
      const { process_stage_id: processStageId, ...updates } = action.payload
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          process_stages: state.currentProcess.process_stages.map((stage) => {
            if (stage.id === processStageId) {
              return { ...stage, ...updates }
            }
            return stage
          }),
        }
      }
    },
    updateInputStepLocally: (
      state,
      action: PayloadAction<
        Omit<
          EditInputStepViewRequestData,
          'user_id' | 'organization_identity_id' | 'choices' | 'template'
        > & {
          choices?: InputStepChoice1[]
          template?: Value0 | null
        }
      >,
    ) => {
      const { input_step_id: inputStepId, ...updates } = action.payload
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          inputs: state.currentProcess.inputs.map((inputStep) => {
            if (inputStep.id === inputStepId) {
              return {
                ...inputStep,
                name: updates.name ?? inputStep.name,
                description: updates.description ?? inputStep.description,
                required: updates.required ?? inputStep.required,
                pinned: updates.pinned ?? inputStep.pinned,
                style: updates.style ?? inputStep.style,
                choices: updates.choices ?? inputStep.choices,
                order: updates.order ?? inputStep.order,
                section: updates.section === undefined ? inputStep.section : updates.section,
                template: updates.template === undefined ? inputStep.template : updates.template,
                calculated_value:
                  updates.calculated_value === undefined
                    ? inputStep.calculated_value
                    : updates.calculated_value,
              } as InputSubstep2
            }
            return inputStep
          }),
        }
      }
    },
    updateDateTimeStepLocally: (
      state,
      action: PayloadAction<
        Omit<EditDateTimeStepViewRequestData, 'user_id' | 'organization_identity_id'>
      >,
    ) => {
      if (state.currentProcess) {
        const { datetime_step_id: dateTimeStepId, ...updates } = action.payload
        state.currentProcess = {
          ...state.currentProcess,
          inputs: state.currentProcess.inputs.map((inputStep) => {
            if (
              inputStep.sub.datetime_step_sub &&
              inputStep.sub.datetime_step_sub.id === dateTimeStepId
            ) {
              return {
                ...inputStep,
                sub: {
                  ...inputStep.sub,
                  datetime_step_sub: {
                    ...inputStep.sub.datetime_step_sub,
                    request_time:
                      updates.request_time ?? inputStep.sub.datetime_step_sub.request_time,
                  } as DatetimeStep,
                } as InputStepSub0,
              }
            }
            return inputStep
          }),
        }
      }
    },
    updateSectionStepLocally: (
      state,
      action: PayloadAction<
        Omit<EditSectionSubStepViewRequestData, 'user_id' | 'organization_identity_id'>
      >,
    ) => {
      const { section_step_id: sectionStepId, ...updates } = action.payload
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          subsections: state.currentProcess.subsections.map((sectionStep) => {
            if (sectionStep.id === sectionStepId) {
              return {
                ...sectionStep,
                name: updates.name ?? sectionStep.name,
                description: updates.description ?? sectionStep.description,
                order: updates.order ?? sectionStep.order,
                button_name:
                  updates.button_name === undefined ? sectionStep.button_name : updates.button_name,
                min: updates.min ?? sectionStep.min,
                max: updates.max ?? sectionStep.max,
                render: updates.render === undefined ? sectionStep.render : updates.render,
                section: updates.section === undefined ? sectionStep.section : updates.section,
              } as SectionSubstep0
            }
            return sectionStep
          }),
        }
      }
    },
    updateFormStyleElementLocally: (
      state,
      action: PayloadAction<
        Omit<EditFormStyleElementRequestData, 'user_id' | 'organization_identity_id'>
      >,
    ) => {
      const { form_style_element_id: formStyleElementId, ...updates } = action.payload

      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          page_breaks: state.currentProcess.page_breaks.map((formStyleElement) => {
            if (formStyleElement.id === formStyleElementId) {
              return {
                ...formStyleElement,
                order: updates.order ?? formStyleElement.order,
              } as FormStyleElement
            }
            return formStyleElement
          }),
        }
      }
    },
    updateFileStepLocally: (
      state,
      action: PayloadAction<
        Omit<EditFileStepViewRequestData, 'user_id' | 'organization_identity_id'>
      >,
    ) => {
      if (state.currentProcess) {
        const { file_step_id: fileStepId, ...updates } = action.payload
        state.currentProcess = {
          ...state.currentProcess,
          inputs: state.currentProcess.inputs.map((inputStep) => {
            if (inputStep.sub.file_step_sub && inputStep.sub.file_step_sub.id === fileStepId) {
              return {
                ...inputStep,
                sub: {
                  ...inputStep.sub,
                  file_step_sub: {
                    ...inputStep.sub.file_step_sub,
                    allowed_file_types:
                      updates.allowed_file_types ?? inputStep.sub.file_step_sub.allowed_file_types,
                  } as FileStep,
                } as InputStepSub0,
              }
            }
            return inputStep
          }),
        }
      }
    },
    updateLocationStepLocally: (
      state,
      action: PayloadAction<
        Omit<EditLocationStepViewRequestData, 'user_id' | 'organization_identity_id'>
      >,
    ) => {
      if (state.currentProcess) {
        const { location_step_id: locationStepId, ...updates } = action.payload

        state.currentProcess = {
          ...state.currentProcess,
          inputs: state.currentProcess.inputs.map((inputStep) => {
            if (
              inputStep.sub.location_step_sub &&
              inputStep.sub.location_step_sub.id === locationStepId
            ) {
              return {
                ...inputStep,
                sub: {
                  ...inputStep.sub,
                  location_step_sub: {
                    ...inputStep.sub.location_step_sub,
                    request_address:
                      updates.request_address ?? inputStep.sub.location_step_sub.request_address,
                    request_coordinates:
                      updates.request_coordinates ??
                      inputStep.sub.location_step_sub.request_coordinates,
                    overlay:
                      updates.overlay === undefined
                        ? inputStep.sub.location_step_sub.overlay
                        : updates.overlay,
                  } as LocationStep,
                } as InputStepSub0,
              }
            }
            return inputStep
          }),
        }
      }
    },
    updateIdentityStepLocally: (
      state,
      action: PayloadAction<
        Omit<EditIdentityStepViewRequestData, 'user_id' | 'organization_identity_id'>
      >,
    ) => {
      if (state.currentProcess) {
        const { identity_step_id: identityStepId, ...updates } = action.payload
        state.currentProcess = {
          ...state.currentProcess,
          inputs: state.currentProcess.inputs.map((inputStep) => {
            if (
              inputStep.sub.identity_step_sub &&
              inputStep.sub.identity_step_sub.id === identityStepId
            ) {
              let metadata = inputStep.sub.identity_step_sub.metadata ?? {}
              if (updates.set_case_requester !== undefined) {
                metadata = {
                  ...metadata,
                  set_case_requester: updates.set_case_requester,
                }
              }

              return {
                ...inputStep,
                sub: {
                  ...inputStep.sub,
                  identity_step_sub: {
                    ...inputStep.sub.identity_step_sub,
                    add_contact: updates.add_contact ?? inputStep.sub.identity_step_sub.add_contact,
                    require_me: updates.require_me ?? inputStep.sub.identity_step_sub.require_me,
                    show_address:
                      updates.show_address ?? inputStep.sub.identity_step_sub.show_address,
                    require_address:
                      updates.require_address ?? inputStep.sub.identity_step_sub.require_address,
                    show_date_of_birth:
                      updates.show_date_of_birth ??
                      inputStep.sub.identity_step_sub.show_date_of_birth,
                    require_date_of_birth:
                      updates.require_date_of_birth ??
                      inputStep.sub.identity_step_sub.require_date_of_birth,
                    show_email: updates.show_email ?? inputStep.sub.identity_step_sub.show_email,
                    require_email:
                      updates.require_email ?? inputStep.sub.identity_step_sub.require_email,
                    verify_email:
                      updates.verify_email ?? inputStep.sub.identity_step_sub.verify_email,
                    show_name: updates.show_name ?? inputStep.sub.identity_step_sub.show_name,
                    require_name:
                      updates.require_name ?? inputStep.sub.identity_step_sub.require_name,
                    show_phone: updates.show_phone ?? inputStep.sub.identity_step_sub.show_phone,
                    require_phone:
                      updates.require_phone ?? inputStep.sub.identity_step_sub.require_phone,
                    metadata: metadata,
                  } as IdentityStep,
                } as InputStepSub0,
              }
            }
            return inputStep
          }),
        }
      }
    },
    updateNumberStepLocally: (
      state,
      action: PayloadAction<
        Omit<EditNumberStepViewRequestData, 'user_id' | 'organization_identity_id'>
      >,
    ) => {
      if (state.currentProcess) {
        const { number_step_id: numberStepId, ...updates } = action.payload
        state.currentProcess = {
          ...state.currentProcess,
          inputs: state.currentProcess.inputs.map((inputStep) => {
            if (
              inputStep.sub.number_step_sub &&
              inputStep.sub.number_step_sub.id === numberStepId
            ) {
              return {
                ...inputStep,
                sub: {
                  ...inputStep.sub,
                  number_step_sub: {
                    ...inputStep.sub.number_step_sub,
                    allow_negative_numbers:
                      updates.allow_negative_numbers ??
                      inputStep.sub.number_step_sub.allow_negative_numbers,
                    allow_zero: updates.allow_zero ?? inputStep.sub.number_step_sub.allow_zero,
                    decimal_places:
                      updates.decimal_places === undefined
                        ? inputStep.sub.number_step_sub.decimal_places
                        : updates.decimal_places,
                    prefix_unit:
                      updates.prefix_unit === undefined
                        ? inputStep.sub.number_step_sub.prefix_unit
                        : updates.prefix_unit,
                    suffix_unit:
                      updates.suffix_unit === undefined
                        ? inputStep.sub.number_step_sub.suffix_unit
                        : updates.suffix_unit,
                  } as NumberStep,
                } as InputStepSub0,
              }
            }
            return inputStep
          }),
        }
      }
    },
    updateSignatureStepLocally: (
      state,
      action: PayloadAction<
        Omit<EditSignatureStepViewRequestData, 'user_id' | 'organization_identity_id'>
      >,
    ) => {
      if (state.currentProcess) {
        const { signature_step_id: signatureStepId, ...updates } = action.payload
        state.currentProcess = {
          ...state.currentProcess,
          inputs: state.currentProcess.inputs.map((inputStep) => {
            if (
              inputStep.sub.signature_step_sub &&
              inputStep.sub.signature_step_sub.id === signatureStepId
            ) {
              return {
                ...inputStep,
                sub: {
                  ...inputStep.sub,
                  signature_step_sub: {
                    ...inputStep.sub.signature_step_sub,
                    check_outdated:
                      updates.check_outdated ?? inputStep.sub.signature_step_sub.check_outdated,
                  } as SignatureStep,
                } as InputStepSub0,
              }
            }
            return inputStep
          }),
        }
      }
    },
  },
  extraReducers: (builder) => {
    const handlePending = (state: ProcessEditState): void => {
      state.status = DatabaseSaveStatus.loading
      state.error = null
      state.errorDetail = null
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleRejected = (state: ProcessEditState, action: PayloadAction<any>): void => {
      state.status = DatabaseSaveStatus.failed
      // The API prefixes every 400 body with 'Bad request: '; it is noise for the user
      const detail =
        typeof action.payload === 'object' && typeof action.payload?.detail === 'string'
          ? (action.payload.detail as string).replace(/^Bad request:\s*/, '')
          : null
      state.error = detail ?? ((action.payload as string) || 'Failed to update step')
      state.errorDetail = detail
    }

    const handleBasicFulfilled = (state: ProcessEditState): void => {
      state.status = DatabaseSaveStatus.succeeded
    }

    const pendingActions = [
      deleteBillStep.pending,
      deleteFormStep.pending,
      deleteProcessStage.pending,
      deleteInputStep.pending,
      deleteSectionStep.pending,
      deleteFormStyleElement.pending,
      deleteStepAssignment.pending,
      editBillStep.pending,
      editDatetimeStep.pending,
      editNumberStep.pending,
      editSignatureStep.pending,
      editFileStep.pending,
      editFormStep.pending,
      editFormStyleElement.pending,
      editIdentityStep.pending,
      editInputStep.pending,
      editProcessStage.pending,
      editSectionStep.pending,
      editStep.pending,
      createProcessStage.pending,
      createInputStep.pending,
      createSectionStep.pending,
      createTagBlock.pending,
      createFormStyleElement.pending,
      copySectionStep.pending,
      createFormBlock.pending,
      createPaymentBlock.pending,
      createStatusBlock.pending,
      createConnector.pending,
      editConnector.pending,
      editStatusBlock.pending,
      editTagBlock.pending,
      editAPIAutomationBlock.pending,
      createRoleBlock.pending,
      editRoleBlock.pending,
      createCommunicationBlock.pending,
      editCommunicationBlock.pending,
      createDocumentBlock.pending,
      createLicenseBlock.pending,
      editLicenseBlock.pending,
      editBlock.pending,
      deleteConnector.pending,
      deleteBlock.pending,
    ] as const
    const basicFulfilledActions = [
      deleteBillStep.fulfilled,
      deleteFormStep.fulfilled,
      deleteProcessStage.fulfilled,
      deleteInputStep.fulfilled,
      deleteFormStyleElement.fulfilled,
      deleteSectionStep.fulfilled,
      deleteStepAssignment.fulfilled,
      editDatetimeStep.fulfilled,
      editFormStep.fulfilled,
      editFormStyleElement.fulfilled,
      editIdentityStep.fulfilled,
      editNumberStep.fulfilled,
      editSignatureStep.fulfilled,
      editFileStep.fulfilled,
      editInputStep.fulfilled,
      editProcessStage.fulfilled,
      editSectionStep.fulfilled,
      editStep.fulfilled,
      editStatusBlock.fulfilled,
      editTagBlock.fulfilled,
      editRoleBlock.fulfilled,
      editRedactionBlock.fulfilled,
      deleteConnector.fulfilled,
      deleteBlock.fulfilled,
    ] as const
    const rejectedActions = [
      deleteBillStep.rejected,
      deleteFormStep.rejected,
      deleteProcessStage.rejected,
      deleteInputStep.rejected,
      deleteFormStyleElement.rejected,
      deleteSectionStep.rejected,
      deleteStepAssignment.rejected,
      editBillStep.rejected,
      editDatetimeStep.rejected,
      editNumberStep.rejected,
      editSignatureStep.rejected,
      editFileStep.rejected,
      editFormStep.rejected,
      editFormStyleElement.rejected,
      editIdentityStep.rejected,
      editInputStep.rejected,
      editProcessStage.rejected,
      editRedactionBlock.rejected,
      editSectionStep.rejected,
      editStep.rejected,
      createProcessStage.rejected,
      createInputStep.rejected,
      createSectionStep.rejected,
      createTagBlock.rejected,
      createFormStyleElement.rejected,
      copySectionStep.rejected,
      createFormBlock.rejected,
      createPaymentBlock.rejected,
      createStatusBlock.rejected,
      createConnector.rejected,
      editConnector.rejected,
      editStatusBlock.rejected,
      editTagBlock.rejected,
      editAPIAutomationBlock.rejected,
      createRoleBlock.rejected,
      editRoleBlock.rejected,
      createCommunicationBlock.rejected,
      editCommunicationBlock.rejected,
      createDocumentBlock.rejected,
      createLicenseBlock.rejected,
      editLicenseBlock.rejected,
      editBlock.rejected,
      deleteConnector.rejected,
      deleteBlock.rejected,
    ] as const

    pendingActions.forEach((actionType) => {
      builder.addCase(actionType, handlePending)
    })

    basicFulfilledActions.forEach((actionType) => {
      builder.addCase(actionType, handleBasicFulfilled)
    })

    rejectedActions.forEach((actionType) => {
      builder.addCase(actionType, handleRejected)
    })

    builder.addCase(editBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { blockId, block } = action.payload
      state.flowBlocks = state.flowBlocks.map((b) => (b.id === blockId ? (block as Block) : b))
    })

    builder.addCase(createTagBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { block, blockId, stepId, tagAutomation, temporaryId } = action.payload

      if (temporaryId) {
        state.flowBlocks = state.flowBlocks.map((b) =>
          b.id === temporaryId ? (block as Block) : b,
        )
        if (state.flowBlockConfigurations) {
          const newTagAutomations = { ...state.flowBlockConfigurations.tag_automations }
          delete newTagAutomations[temporaryId]
          newTagAutomations[blockId as string] = {
            id: tagAutomation.id as string,
            block: blockId as string,
            tag: tagAutomation.tag ?? null,
            apply: tagAutomation.apply ?? null,
            metadata: tagAutomation.metadata ?? null,
          }
          state.flowBlockConfigurations = {
            ...state.flowBlockConfigurations,
            tag_automations: newTagAutomations,
          }
        }
      }

      if (stepId && state.currentProcess) {
        const formStep = state.currentProcess?.form_steps?.find((step) => step.step === stepId)
        const billStep = state.currentProcess?.bill_steps?.find((step) => step.step === stepId)

        if (formStep) {
          const updatedFormStep = {
            ...formStep,
            calculated_tags: formStep.calculated_tags.map((calculatedTag) => {
              if (
                calculatedTag.tag === tagAutomation.tag &&
                calculatedTag.expression === tagAutomation.apply
              ) {
                return {
                  ...calculatedTag,
                  block: blockId,
                }
              }
              return calculatedTag
            }),
          }

          state.currentProcess = {
            ...state.currentProcess,
            form_steps: state.currentProcess.form_steps.map((step) =>
              step === formStep ? updatedFormStep : step,
            ),
          }
        }

        if (billStep) {
          const updatedBillStep = {
            ...billStep,
            calculated_tags: billStep.calculated_tags.map((calculatedTag) => {
              if (
                calculatedTag.tag === tagAutomation.tag &&
                calculatedTag.expression === tagAutomation.apply
              ) {
                return {
                  ...calculatedTag,
                  block: blockId,
                }
              }
              return calculatedTag
            }),
          }

          state.currentProcess = {
            ...state.currentProcess,
            bill_steps: state.currentProcess.bill_steps.map((step) =>
              step === billStep ? updatedBillStep : step,
            ),
          }
        }
      }
    })

    builder.addCase(createRoleBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { block, blockId, roleAutomation, temporaryId } = action.payload

      if (temporaryId) {
        state.flowBlocks = state.flowBlocks.map((b) =>
          b.id === temporaryId ? (block as Block) : b,
        )
        if (state.flowBlockConfigurations) {
          const newRoleAutomations = { ...state.flowBlockConfigurations.role_automations }
          delete newRoleAutomations[temporaryId]
          newRoleAutomations[blockId as string] = {
            id: roleAutomation.id as string,
            block: blockId as string,
            identity_expression: roleAutomation.identity_expression ?? null,
            metadata: roleAutomation.metadata ?? null,
            case_role_action: roleAutomation.case_role_action,
          }
          state.flowBlockConfigurations = {
            ...state.flowBlockConfigurations,
            role_automations: newRoleAutomations,
          }
        }
      }
    })

    builder.addCase(createCommunicationBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { block, blockId, communicationAutomation, temporaryId } = action.payload
      const realBlockId = blockId as string

      if (temporaryId) {
        state.flowBlocks = state.flowBlocks.map((b) =>
          b.id === temporaryId ? (block as Block) : b,
        )
      } else {
        state.flowBlocks.push(block as Block)
      }

      if (state.flowBlockConfigurations) {
        const newCommunicationAutomations = {
          ...state.flowBlockConfigurations.communication_automations,
        }
        if (temporaryId) {
          delete newCommunicationAutomations[temporaryId]
        }
        newCommunicationAutomations[realBlockId] = communicationAutomation
        state.flowBlockConfigurations = {
          ...state.flowBlockConfigurations,
          communication_automations: newCommunicationAutomations,
        }
      }

      if (temporaryId && state.communicationAutomations[temporaryId]) {
        const nextAutomations = { ...state.communicationAutomations }
        delete nextAutomations[temporaryId]
        nextAutomations[realBlockId] = communicationAutomation
        state.communicationAutomations = nextAutomations
      } else {
        state.communicationAutomations = {
          ...state.communicationAutomations,
          [realBlockId]: communicationAutomation,
        }
      }

      if (temporaryId && state.selectedCommunicationBlock === (temporaryId as BlockId)) {
        state.selectedCommunicationBlock = realBlockId as BlockId
      }
      if (temporaryId && state.selectedRedactionBlock === (temporaryId as BlockId)) {
        state.selectedRedactionBlock = realBlockId as BlockId
      }

      if (state.currentProcess) {
        const existingBlocks = state.currentProcess.blocks.filter((b) =>
          temporaryId ? b.id !== temporaryId : true,
        )
        state.currentProcess = {
          ...state.currentProcess,
          blocks: [...existingBlocks, block as Block],
        }
      }
    })

    builder.addCase(createDocumentBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { block, temporaryId } = action.payload

      if (temporaryId) {
        state.flowBlocks = state.flowBlocks.map((b) =>
          b.id === temporaryId ? (block as Block) : b,
        )
      } else {
        state.flowBlocks.push(block as Block)
      }

      if (state.currentProcess) {
        const existingBlocks = state.currentProcess.blocks.filter((b) =>
          temporaryId ? b.id !== temporaryId : true,
        )
        state.currentProcess = {
          ...state.currentProcess,
          blocks: [...existingBlocks, block as Block],
        }
      }
    })

    builder.addCase(createDemoBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { block, demoBlock, temporaryId } = action.payload

      if (temporaryId) {
        state.flowBlocks = state.flowBlocks.map((b) =>
          b.id === temporaryId ? (block as Block) : b,
        )
      } else {
        state.flowBlocks.push(block as Block)
      }

      if (state.flowBlockConfigurations) {
        const newDemoBlocks = { ...state.flowBlockConfigurations.demo_blocks }
        if (temporaryId) delete newDemoBlocks[temporaryId]
        newDemoBlocks[block.id] = demoBlock
        state.flowBlockConfigurations = {
          ...state.flowBlockConfigurations,
          demo_blocks: newDemoBlocks,
        }
      }

      if (state.currentProcess) {
        const existingBlocks = state.currentProcess.blocks.filter((b) =>
          temporaryId ? b.id !== temporaryId : true,
        )
        state.currentProcess = {
          ...state.currentProcess,
          blocks: [...existingBlocks, block as Block],
        }
      }
    })

    builder.addCase(createAPIAutomationBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { block, apiAutomation, temporaryId } = action.payload

      if (temporaryId) {
        state.flowBlocks = state.flowBlocks.map((b) =>
          b.id === temporaryId ? (block as Block) : b,
        )
      } else {
        state.flowBlocks.push(block as Block)
      }

      if (state.flowBlockConfigurations) {
        const newAPIAutomations = { ...state.flowBlockConfigurations.api_automations }
        if (temporaryId) delete newAPIAutomations[temporaryId]
        newAPIAutomations[block.id] = apiAutomation
        state.flowBlockConfigurations = {
          ...state.flowBlockConfigurations,
          api_automations: newAPIAutomations,
        }
      }

      if (state.currentProcess) {
        const existingBlocks = state.currentProcess.blocks.filter((b) =>
          temporaryId ? b.id !== temporaryId : true,
        )
        state.currentProcess = {
          ...state.currentProcess,
          blocks: [...existingBlocks, block as Block],
        }
      }
    })

    builder.addCase(editAPIAutomationBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { blockId, apiAutomation } = action.payload
      if (state.flowBlockConfigurations) {
        state.flowBlockConfigurations = {
          ...state.flowBlockConfigurations,
          api_automations: {
            ...state.flowBlockConfigurations.api_automations,
            [blockId as string]: apiAutomation,
          },
        }
      }
    })

    builder.addCase(editDemoBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { blockId, demoBlock } = action.payload
      if (state.flowBlockConfigurations) {
        state.flowBlockConfigurations = {
          ...state.flowBlockConfigurations,
          demo_blocks: {
            ...state.flowBlockConfigurations.demo_blocks,
            [blockId as string]: demoBlock,
          },
        }
      }
    })

    builder.addCase(createRedactionBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { block, redactionBlock, temporaryId } = action.payload

      if (temporaryId) {
        state.flowBlocks = state.flowBlocks.map((flowBlock) =>
          flowBlock.id === temporaryId ? (block as Block) : flowBlock,
        )
      } else {
        state.flowBlocks.push(block as Block)
      }

      if (state.flowBlockConfigurations) {
        const newRedactionBlocks = { ...state.flowBlockConfigurations.redaction_blocks }
        if (temporaryId) delete newRedactionBlocks[temporaryId]
        newRedactionBlocks[block.id] = redactionBlock
        state.flowBlockConfigurations = {
          ...state.flowBlockConfigurations,
          redaction_blocks: newRedactionBlocks,
        }
      }

      if (state.currentProcess) {
        const existingBlocks = state.currentProcess.blocks.filter((processBlock) =>
          temporaryId ? processBlock.id !== temporaryId : true,
        )
        state.currentProcess = {
          ...state.currentProcess,
          blocks: [...existingBlocks, block as Block],
        }
      }
    })

    builder.addCase(createReviewBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { block, blockId, reviewBlock, connectors, gateExpressions, temporaryId } =
        action.payload
      const realBlockId = blockId as string

      if (temporaryId) {
        state.flowBlocks = state.flowBlocks.map((b) =>
          b.id === temporaryId ? (block as Block) : b,
        )
      } else {
        state.flowBlocks.push(block as Block)
      }
      state.flowConnectors = state.flowConnectors.concat(connectors as Connector[])
      gateExpressions.forEach((expression) => {
        state.expressions[expression.id as ExpressionId] = expression
      })

      if (state.flowBlockConfigurations) {
        const newReviewBlocks = { ...state.flowBlockConfigurations.review_blocks }
        if (temporaryId) {
          delete newReviewBlocks[temporaryId]
        }
        newReviewBlocks[realBlockId] = reviewBlock
        state.flowBlockConfigurations = {
          ...state.flowBlockConfigurations,
          review_blocks: newReviewBlocks,
        }
      }

      if (state.currentProcess) {
        const existingBlocks = state.currentProcess.blocks.filter((b) =>
          temporaryId ? b.id !== temporaryId : true,
        )
        state.currentProcess = {
          ...state.currentProcess,
          blocks: [...existingBlocks, block as Block],
        }
      }
    })

    builder.addCase(editReviewBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { blockId, reviewBlock, connectors, gateExpressions, deletedConnectorIds } =
        action.payload
      const deletedIds = new Set<string>(deletedConnectorIds)
      state.flowConnectors.forEach((c) => {
        const gate =
          deletedIds.has(c.id) && c.gate ? state.expressions[c.gate as ExpressionId] : undefined
        if (gate) {
          delete state.expressions[gate.parameter_one as ExpressionId]
          delete state.expressions[gate.parameter_two as ExpressionId]
          delete state.expressions[gate.id as ExpressionId]
        }
      })
      state.flowConnectors = state.flowConnectors
        .filter((c) => !deletedIds.has(c.id))
        .concat(connectors as Connector[])
      gateExpressions.forEach((expression) => {
        state.expressions[expression.id as ExpressionId] = expression
      })
      if (state.flowBlockConfigurations) {
        state.flowBlockConfigurations = {
          ...state.flowBlockConfigurations,
          review_blocks: {
            ...state.flowBlockConfigurations.review_blocks,
            [blockId as string]: reviewBlock,
          },
        }
      }
    })

    builder.addCase(createLicenseBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { block, licenseBlock, temporaryId } = action.payload

      if (temporaryId) {
        state.flowBlocks = state.flowBlocks.map((b) =>
          b.id === temporaryId ? (block as Block) : b,
        )
      } else {
        state.flowBlocks.push(block as Block)
      }

      if (state.flowBlockConfigurations) {
        const newLicenseBlocks = { ...state.flowBlockConfigurations.license_blocks }
        if (temporaryId) delete newLicenseBlocks[temporaryId]
        newLicenseBlocks[block.id] = licenseBlock
        state.flowBlockConfigurations = {
          ...state.flowBlockConfigurations,
          license_blocks: newLicenseBlocks,
        }
      }

      if (state.currentProcess) {
        const existingBlocks = state.currentProcess.blocks.filter((b) =>
          temporaryId ? b.id !== temporaryId : true,
        )
        state.currentProcess = {
          ...state.currentProcess,
          blocks: [...existingBlocks, block as Block],
        }
      }
    })

    builder.addCase(editLicenseBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { blockId, licenseBlock } = action.payload
      if (state.flowBlockConfigurations) {
        state.flowBlockConfigurations = {
          ...state.flowBlockConfigurations,
          license_blocks: {
            ...state.flowBlockConfigurations.license_blocks,
            [blockId as string]: licenseBlock,
          },
        }
      }
    })

    builder.addCase(editCommunicationBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { blockId, communicationAutomation } = action.payload
      state.communicationAutomations = {
        ...state.communicationAutomations,
        [blockId as string]: communicationAutomation,
      }
      if (state.flowBlockConfigurations?.communication_automations[blockId as string]) {
        state.flowBlockConfigurations = {
          ...state.flowBlockConfigurations,
          communication_automations: {
            ...state.flowBlockConfigurations.communication_automations,
            [blockId as string]: communicationAutomation,
          },
        }
      }
    })

    builder.addCase(createProcessStage.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { processStageId, temporaryId } = action.payload
      if (state.currentProcess) {
        const updatedProcessStages = [...state.currentProcess.process_stages]
        state.currentProcess = {
          ...state.currentProcess,
          process_stages: updatedProcessStages.map((processStage) => {
            if (processStage.id === temporaryId) {
              return {
                ...processStage,
                id: processStageId,
              }
            }
            return processStage
          }),
        }
      }
    })

    builder.addCase(createInputStep.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const {
        inputStepId,
        temporaryId,
        datetimeStepSub,
        identityStepSub,
        numberStepSub,
        fileStepSub,
        locationStepSub,
      } = action.payload
      const createdInputStep = state.currentProcess?.inputs.find(
        (inputStep) => inputStep.id === temporaryId,
      )
      if (createdInputStep && state.currentProcess) {
        const updatedInputSteps = [...state.currentProcess.inputs]
        state.currentProcess = {
          ...state.currentProcess,
          inputs: updatedInputSteps.map((inputStep) => {
            if (inputStep.id === temporaryId) {
              return {
                ...createdInputStep,
                id: inputStepId,
                sub: {
                  ...createdInputStep.sub,
                  datetime_step_sub: datetimeStepSub,
                  identity_step_sub: identityStepSub,
                  number_step_sub: numberStepSub,
                  file_step_sub: fileStepSub,
                  location_step_sub: locationStepSub,
                } as InputStepSub0,
              }
            }
            return inputStep
          }),
        }
      }
    })

    builder.addCase(createSectionStep.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { sectionStepId, temporaryId } = action.payload
      const createdSectionStep = state.currentProcess?.subsections.find(
        (sectionStep) => sectionStep.id === temporaryId,
      )
      if (createdSectionStep && state.currentProcess) {
        const updatedSectionSteps = [...state.currentProcess.subsections]
        state.currentProcess = {
          ...state.currentProcess,
          subsections: updatedSectionSteps.map((sectionStep) => {
            if (sectionStep.id === temporaryId) {
              return {
                ...createdSectionStep,
                id: sectionStepId,
              }
            }
            return sectionStep
          }),
        }
      }
    })

    builder.addCase(createFormStyleElement.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { formStyleElementId, temporaryId } = action.payload
      const createdFormStyleElement = state.currentProcess?.page_breaks.find(
        (pageBreak) => pageBreak.id === temporaryId,
      )
      if (createdFormStyleElement && state.currentProcess) {
        const updatedPageBreaks = [...state.currentProcess.page_breaks]
        state.currentProcess = {
          ...state.currentProcess,
          page_breaks: updatedPageBreaks.map((pageBreak) => {
            if (pageBreak.id === temporaryId) {
              return {
                ...createdFormStyleElement,
                id: formStyleElementId,
              } as FormStyleElement
            }
            return pageBreak
          }),
        }
      }
    })

    builder.addCase(editBillStep.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { stepId, charges } = action.payload
      const updatedBillStep = state.currentProcess?.bill_steps.find(
        (billStep) => billStep.id === stepId,
      )
      if (updatedBillStep && state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          bill_steps: [...state.currentProcess.bill_steps].map((billStep) => {
            if (billStep.id === stepId) {
              return {
                ...billStep,
                charges: charges ? [...charges] : billStep.charges,
              }
            }
            return billStep
          }),
        }
      }
    })

    builder.addCase(copySectionStep.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { copiedSections, copiedInputs } = action.payload
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          inputs: state.currentProcess.inputs.map((inputStep) => {
            if (inputStep.id.endsWith('-copy')) {
              const newInputSubstep2 = copiedInputs[inputStep.id.split('-copy')[0]]
              return {
                ...inputStep,
                id: newInputSubstep2.id,
                section: inputStep.section
                  ? copiedSections[inputStep.section.split('-copy')[0]]
                  : null,
                sub: newInputSubstep2.sub,
              }
            }
            return inputStep
          }),
          subsections: state.currentProcess.subsections.map((sectionStep) => {
            if (sectionStep.id.endsWith('-copy')) {
              return {
                ...sectionStep,
                id: copiedSections[sectionStep.id.split('-copy')[0]],
                section: sectionStep.section
                  ? copiedSections[sectionStep.section.split('-copy')[0]]
                  : null,
              }
            }
            return sectionStep
          }),
        }
      }
    })

    builder.addCase(createFormBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { block, formStep, temporaryId } = action.payload
      state.flowBlocks = state.flowBlocks.map((b) => (b.id === temporaryId ? (block as Block) : b))
      if (state.flowBlockConfigurations) {
        state.flowBlockConfigurations.form_steps[block.id as string] = {
          id: formStep.id as string,
          step: formStep.step as string,
          block: formStep.block as string,
          name: formStep.name,
          description: formStep.description,
          external: formStep.external,
          due: formStep.due,
          sla: formStep.sla,
          button_name: formStep.button_name,
          requires_submission_confirmation: formStep.requires_submission_confirmation,
        }
      }

      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          form_steps: [
            ...state.currentProcess.form_steps,
            {
              id: formStep.id,
              description: formStep.description,
              calculated_tags: [],
              process: state.currentProcess.id,
              process_stage: null,
              code: '',
              step: formStep.step,
              block: formStep.block,
              name: formStep.name,
              order: state.currentProcess.form_steps.length,
              external: formStep.external,
              due: formStep.due ?? null,
              sla: formStep.sla ?? null,
              button_name: formStep.button_name ?? null,
              set_status: null,
              added_at: null,
              removed_at: null,
              ai_review_prompt: null,
              ai_review_input_step_ids: [],
              ai_assign_case_prompt: null,
              ai_assign_case_input_step_ids: [],
              requires_submission_confirmation: formStep.requires_submission_confirmation ?? false,
              generation_task_id: null,
            } as FormStep0,
          ],
        }
      }
    })

    builder.addCase(createPaymentBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { block, billStep, temporaryId } = action.payload
      state.flowBlocks = state.flowBlocks.map((b) => (b.id === temporaryId ? (block as Block) : b))
      if (state.flowBlockConfigurations) {
        state.flowBlockConfigurations.bill_steps[block.id as string] = {
          id: billStep.id as string,
          step: billStep.step as string,
          block: billStep.block as string,
          name: billStep.name,
          description: billStep.description,
          installments: (billStep as unknown as { installments: number }).installments ?? 1,
        }
      }
      if (state.currentProcess) {
        state.currentProcess = {
          ...state.currentProcess,
          bill_steps: [
            ...state.currentProcess.bill_steps,
            {
              charges: [],
              id: billStep.id,
              process: state.currentProcess.id,
              process_stage: null,
              code: '',
              step: billStep.step,
              block: billStep.block,
              name: billStep.name,
              description: billStep.description,
              installments: (billStep as unknown as { installments: number }).installments ?? 1,
              order: state.currentProcess.bill_steps.length,
              account: (billStep as unknown as { account: string | null }).account ?? null,
              distribution:
                (billStep as unknown as { distribution: string | null }).distribution ?? null,
              set_status: null,
              added_at: null,
              removed_at: null,
              allow_ach: (billStep as unknown as { allow_ach: boolean }).allow_ach ?? true,
              calculated_tags: [],
            } as BillStep1,
          ],
        }
      }
    })

    builder.addCase(createStatusBlock.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { block, status, temporaryId } = action.payload
      state.flowBlocks = state.flowBlocks.map((b) => (b.id === temporaryId ? (block as Block) : b))
      if (state.flowBlockConfigurations && block.id) {
        delete state.flowBlockConfigurations.status_automations[temporaryId]
        state.flowBlockConfigurations.status_automations[block.id as string] = {
          id: block.id as string,
          block: block.id as string,
          // TODO: Use initialStatus once backend supports it
          status: status ?? null,
        }
      }
    })

    builder.addCase(createConnector.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { connector, temporaryId, gateExpressions } = action.payload
      if (temporaryId) {
        state.flowConnectors = state.flowConnectors.map((c) =>
          c.id === temporaryId ? (connector as Connector) : c,
        )
      }
      gateExpressions.forEach((expression) => {
        state.expressions[expression.id as ExpressionId] = expression
      })
    })

    builder.addCase(editConnector.fulfilled, (state, action) => {
      state.status = DatabaseSaveStatus.succeeded
      const { oldConnectorId, connector, gateExpressions } = action.payload
      state.flowConnectors = state.flowConnectors.map((c) =>
        c.id === (oldConnectorId as string) ? (connector as Connector) : c,
      )
      gateExpressions.forEach((expression) => {
        state.expressions[expression.id as ExpressionId] = expression
      })
    })
  },
})

export const {
  setCurrentProcess,
  setSelectedRedactionBlock,
  setSelectedStep,
  setSelectedStepCheckboxes,
  setSelectedBlockCheckboxes,
  updateBlockStageLocally,
  setSelectedFormSubstep,
  setHasTrackerStageError,
  setTrackerStageErrorMessage,
  setUserId,
  setExpressions,
  setModal,
  setStepNotifications,
  setSelectedStage,
  setSnackbarConfiguration,
  setFlowIssues,
  clearProcessEdit,
  setFlowData,
  setBlockJoinTypeLocally,
  setBlockJoinExpressionLocally,
  createBlockLocally,
  deleteBlockLocally,
  deleteConnectorLocally,
  addConnectorLocally,
  updateConnectorLocally,
  createInputStepLocally,
  createProcessStageLocally,
  createSectionStepLocally,
  createFormStyleElementLocally,
  copySectionStepLocally,
  addExpressionsLocally,
  editExpressionLocally,
  deleteExpressionsLocally,
  deleteInputStepLocally,
  deleteFormStyleElementLocally,
  deleteSectionStepLocally,
  deleteStepAssignmentLocally,
  deleteStepLocally,
  deleteProcessStageLocally,
  updateBillStepLocally,
  updateDateTimeStepLocally,
  updateFormStepLocally,
  updateFormStyleElementLocally,
  updateFileStepLocally,
  updateIdentityStepLocally,
  updateLocationStepLocally,
  updateNumberStepLocally,
  updateSignatureStepLocally,
  updateInputStepLocally,
  updateProcessStageLocally,
  updateSectionStepLocally,
  updateStatusBlockLocally,
  updateTagBlockLocally,
  updateRoleBlockLocally,
  updateLicenseBlockLocally,
  updateDemoBlockLocally,
  updateRedactionBlockLocally,
  removeAutomationBlockLocally,
  updateStepLocally,
  setSelectedCommunicationBlock,
  setCommunicationAutomations,
  setEmailCapability,
  updateCommunicationBlockLocally,
  removeCommunicationBlockLocally,
  updateDocumentStepLocally,
} = processEditSlice.actions

export default processEditSlice.reducer
